

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import data from "../../data/sampleData.json";
//import heart from "../../images/loading.gif";
import "./searchbar.css";
import Suggestions from "../suggestion/Suggestions";
import AskNowPopup from "./AskNowPopup";

import loadingImage from "../../images/loadingimage-2.gif";
import Footer from "../footer/Footer";
import Sidebar from "../sidebar/Sidebar";
import MiniSidebar from "../sidebar/MiniSidebar";
import FeedbackPopup from "../feedback/FeedbackPopup";
import dmilogosmall from "../../images/DeepMedIQ-small.jpeg";
import MultimodalModal from "./MultimodalModal";
import { isServiceWorkerStreamingSupported, createProxiedStream } from "../../utils/streamWorkerClient";
import { API_URL } from "../../utils/apiConfig";
import SearchGateModal, { hasReachedFreeLimit, incrementFreeSearchCount, clearFreeSearchCount } from "./SearchGateModal";

const VIDEO_CHUNK_TYPES = new Set(['video_transcript', 'video_keyframe', 'video_slide']);

/**
 * Generalized citation normalizer.
 * Handles ANY bracket containing Source N and/or PubMed Source N (with optional PMID),
 * in any combination, any order, any count.
 * All citations are rendered as plain [N] — continuous renumbering happens in the citations event.
 */
const normalizeCitations = (text) => {
  if (!text) return text;

  // Strip raw chunk/page references like [19355 1..14 (2020)] that leak through from the API.
  // Pattern: bracket containing digits, spaces, dots, and a 4-digit year in parens — but no [N] style content.
  text = text.replace(/\[\d[\d\s.]*\(\d{4}\)\]/g, '');

  return text.replace(
    /\[([^\[\]]*(?:Source|PubMed)[^\[\]]*)\]/g,
    (match, inner) => {
      // Extract all "Source N" entries (Cosmos DB sources)
      const sourceMatches = inner.match(/(?<!PubMed\s)Source\s+(\d+)/g) || [];
      // Extract all "PubMed Source N" entries
      const pubmedSourceMatches = inner.match(/PubMed\s+Source\s+(\d+)/g) || [];
      // Extract all "PubMed N" entries (without "Source")
      const pubmedPlainMatches = inner.match(/PubMed\s+(\d+)(?!\w)/g) || [];

      const parts = [];

      // Cosmos DB sources — rendered as [N]
      sourceMatches.forEach(m => {
        const num = m.match(/\d+/)[0];
        parts.push(`[${num}]`);
      });

      // PubMed sources — also rendered as plain [N]
      if (sourceMatches.length === 0) {
        // Try "PubMed Source N" first, then "PubMed N"
        const pubmedMatches = pubmedSourceMatches.length > 0 ? pubmedSourceMatches : pubmedPlainMatches;
        pubmedMatches.forEach(m => {
          const num = m.match(/\d+/)[0];
          parts.push(`[${num}]`);
        });
      }

      return parts.length > 0 ? parts.join('') : match;
    }
  );
};

/**
 * Expand a citation string that may contain ranges and/or comma-separated numbers.
 * e.g. "1-6,12-14" → ["1","2","3","4","5","6","12","13","14"]
 *      "7-11,13"   → ["7","8","9","10","11","13"]
 *      "9"         → ["9"]
 */
const expandCitationRanges = (raw) => {
  const nums = [];
  raw.split(',').forEach(part => {
    const trimmed = part.trim();
    const rangeMatch = trimmed.match(/^(\d+)\s*[-\u2013]\s*(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1]);
      const end = parseInt(rangeMatch[2]);
      for (let i = start; i <= end; i++) nums.push(String(i));
    } else if (/^\d+$/.test(trimmed)) {
      nums.push(trimmed);
    }
  });
  return nums;
};

/**
 * Renumber a slice of references contiguously (1..N) based on first-appearance
 * order in the answer text, and rewrite the answer's inline markers to match.
 *
 * The LLM cites by the source numbers it saw in its context (e.g. [7], [10],
 * [PubMed 2]) — which after backend dedup + post-filter can leave the
 * displayed References panel with sparse, non-contiguous labels. This helper
 * collapses them back to a clean academic-paper-style 1, 2, 3 sequence,
 * unifying guideline and PubMed citations under a single [N] namespace.
 *
 * Returns: { refs (mutated with new .number), newAnswer, remap, pubMap }
 *  - refs:       reordered + renumbered (1..N) in first-appearance order
 *  - newAnswer:  fullAnswer with [old]→[new], [PubMed old]→[new]
 *  - remap:      Map<int, int>      old guideline number → new int
 *  - pubMap:     Map<int, int>      old PubMed number    → new int
 */
const renumberRefsByFirstAppearance = (refs, text) => {
  if (!refs.length) return { refs: [], newAnswer: text, remap: new Map(), pubMap: new Map() };

  // Index refs by their current number for quick lookup
  const guidelineRefByNum = new Map(); // int → ref
  const pubmedRefByNum = new Map();    // int → ref
  refs.forEach(r => {
    if (typeof r.number === 'string' && r.number.startsWith('PubMed ')) {
      const n = parseInt(r.number.replace('PubMed ', ''));
      if (!Number.isNaN(n)) pubmedRefByNum.set(n, r);
    } else if (typeof r.number === 'number') {
      guidelineRefByNum.set(r.number, r);
    } else if (typeof r.number === 'string' && /^\d+$/.test(r.number)) {
      guidelineRefByNum.set(parseInt(r.number), r);
    }
  });

  // Walk the answer in order; emit each unique marker once, assigning the
  // next available [N] number. Compound markers like [7,10] are expanded so
  // each underlying source gets its own slot.
  const seenGuideline = new Map(); // old int → new int
  const seenPubmed = new Map();    // old int → new int
  const orderedRefs = [];
  let next = 1;

  // Single pass — order matters, so we use the catch-all marker regex
  const anyMarker = /\[(?:PubMed\s+)?[\d,\s\-–]+\]/g;
  let m;
  while ((m = anyMarker.exec(text)) !== null) {
    const isPub = m[0].startsWith('[PubMed');
    const inner = m[0].replace(/^\[(?:PubMed\s+)?/, '').replace(/\]$/, '');
    for (const numStr of expandCitationRanges(inner)) {
      const n = parseInt(numStr);
      if (isPub) {
        if (seenPubmed.has(n)) continue;
        const ref = pubmedRefByNum.get(n);
        if (!ref) continue; // not in cited set — skip
        seenPubmed.set(n, next);
        ref.number = next;
        orderedRefs.push(ref);
        next++;
      } else {
        if (seenGuideline.has(n)) continue;
        const ref = guidelineRefByNum.get(n);
        if (!ref) continue;
        seenGuideline.set(n, next);
        ref.number = next;
        orderedRefs.push(ref);
        next++;
      }
    }
  }

  // Any refs not encountered in the text get appended at the end (rare;
  // happens if backend listed a ref as cited but its number never appears
  // in the displayed answer text).
  refs.forEach(r => {
    if (!orderedRefs.includes(r)) {
      r.number = next;
      orderedRefs.push(r);
      next++;
    }
  });

  // Rewrite the answer: collapse both guideline [N] and [PubMed N] forms
  // into the unified [new-N] namespace. Compound markers like [7,10] expand
  // to [1][2]; mixed-family or unmapped entries are dropped.
  const newAnswer = text.replace(anyMarker, (raw) => {
    const isPub = raw.startsWith('[PubMed');
    const inner = raw.replace(/^\[(?:PubMed\s+)?/, '').replace(/\]$/, '');
    const expanded = expandCitationRanges(inner)
      .map(s => parseInt(s))
      .map(n => isPub ? seenPubmed.get(n) : seenGuideline.get(n))
      .filter(n => n != null);
    const unique = [...new Set(expanded)];
    if (!unique.length) return raw; // nothing mapped — keep raw to avoid silent loss
    return unique.map(n => `[${n}]`).join('');
  });

  return {
    refs: orderedRefs,
    newAnswer,
    remap: seenGuideline,
    pubMap: seenPubmed,
  };
};

/**
 * Extract the set of citation identifiers that actually appear as inline markers
 * in the (already-normalized) answer text. Returns a Set that mixes integers
 * (for bare [N] markers — guidelines) and strings like "PubMed N" (for
 * [PubMed N] markers — PubMed articles). Any reference whose `number` field
 * is NOT in this set is considered "background" — retrieved but not cited.
 *
 * Handles compound and range forms: "[1]", "[1,2,3]", "[1-5]", "[1, 3, 7-9]",
 * "[PubMed 1]", "[PubMed 1,2]".
 *
 * The mixed-type set works because the references built from formatted_citations
 * carry guideline numbers as ints (1, 2, ...) and PubMed numbers as strings
 * ("PubMed 1", "PubMed 2", ...). Set.has uses strict equality, so each ref
 * type matches its own marker family.
 */
const extractCitedNumbers = (text) => {
  const cited = new Set();
  if (!text) return cited;
  // Guideline [N] / [N,M] / [N-M] — negative lookbehind excludes [PubMed N]
  const guidelineRe = /(?<!PubMed )(?<!PubMed)\[(\d[\d,\s\-–]*\d|\d+)\]/g;
  let m;
  while ((m = guidelineRe.exec(text)) !== null) {
    expandCitationRanges(m[1]).forEach(n => cited.add(parseInt(n)));
  }
  // PubMed [PubMed N] / [PubMed N,M]
  const pubmedRe = /\[PubMed\s+([\d,\s\-–]+)\]/g;
  while ((m = pubmedRe.exec(text)) !== null) {
    expandCitationRanges(m[1]).forEach(n => cited.add(`PubMed ${n}`));
  }
  return cited;
};

/**
 * Deduplicate a references array by DOI.
 * When two entries share the same DOI, the one with an image is preferred.
 * Entries without a DOI are always kept as-is.
 * Original `number` values are preserved and insertion order is maintained.
 * Returns { refs, remapNumbers } so callers can rewrite inline citations.
 */
// Strip common DOI URL prefixes so bare DOIs and URL-form DOIs match each other.
const normalizeDoi = (raw) => {
  if (!raw) return '';
  return raw.toString().trim().toLowerCase()
    .replace(/^https?:\/\/(dx\.)?doi\.org\//, '');
};

// Derive a deduplication key: prefer DOI, fall back to pubmed_id, then exact title.
// Video citations key on source_link (timestamped URL) so two clips from the same
// video at different timestamps are never collapsed into one.
const VIDEO_CHUNK_TYPES_FOR_DEDUP = new Set(['video_transcript', 'video_keyframe', 'video_slide']);
const refDedupeKey = (ref) => {
  if (VIDEO_CHUNK_TYPES_FOR_DEDUP.has(ref.chunk_type)) {
    const url = ref.source_link || ref.video_url;
    return url ? `video:${url}` : null;
  }
  const doi = normalizeDoi(ref.doi);
  if (doi) return `doi:${doi}`;
  if (ref.pubmed_id) return `pmid:${ref.pubmed_id}`;
  const title = ref.title?.trim().toLowerCase();
  if (title) return `title:${title}`;
  return null; // nothing to key on — always keep
};

/**
 * Deduplicate references by DOI / PMID / title.
 * The first-seen entry for each key is the canonical one and keeps its number.
 * If a later duplicate carries an image that the canonical lacks, the canonical is
 * upgraded in-place with the image fields (number preserved).
 * Returns { refs, remapNumbers } where remapNumbers maps each removed ref.number
 * to the canonical ref.number so callers can rewrite inline citations.
 */
const deduplicateReferencesByDoi = (refs) => {
  const seen = new Map(); // dedup key → index in result array
  const remapNumbers = new Map(); // duplicate number → canonical number
  const result = [];

  for (const ref of refs) {
    const key = refDedupeKey(ref);
    if (!key) {
      result.push(ref);
      continue;
    }

    if (!seen.has(key)) {
      seen.set(key, result.length);
      result.push(ref);
    } else {
      const existingIdx = seen.get(key);
      const canonical = result[existingIdx];
      // Always remap this duplicate's number to the canonical number
      remapNumbers.set(ref.number, canonical.number);
      // If duplicate has an image and canonical doesn't, upgrade canonical in-place
      const canonicalHasImage = !!(canonical.image_blob_url || canonical.is_multimodal);
      const duplicateHasImage = !!(ref.image_blob_url || ref.is_multimodal);
      if (duplicateHasImage && !canonicalHasImage) {
        result[existingIdx] = {
          ...ref,
          number: canonical.number, // keep the canonical's number
        };
      }
    }
  }

  return { refs: result, remapNumbers };
};

/**
 * Remap duplicate citation numbers to their canonical number in answer text.
 * e.g. remapNumbers={3→1}:  "[3]"   → "[1]"
 *                            "[1,3,5]" → "[1][5]"  (deduped within bracket)
 *                            "[1-5]"   → "[1][2][4][5]" with 3→1 → "[1][2][4][5]"
 */
const remapInlineCitations = (text, remapNumbers) => {
  if (!remapNumbers.size) return text;

  return text.replace(/\[(\d[\d,\s\-\u2013]*\d|\d+)\]/g, (match, inner) => {
    const nums = expandCitationRanges(inner);
    // Remap each number, then deduplicate within the bracket (preserving order)
    const seen = new Set();
    const remapped = [];
    for (const n of nums) {
      const mapped = String(remapNumbers.get(parseInt(n)) ?? parseInt(n));
      if (!seen.has(mapped)) {
        seen.add(mapped);
        remapped.push(mapped);
      }
    }
    if (remapped.join(',') === nums.join(',')) return match; // nothing changed
    if (!remapped.length) return '';
    return remapped.map(n => `[${n}]`).join('');
  });
};

/**
 * Detect section type from heading text for styling and default expand/collapse.
 */
const detectSectionType = (heading) => {
  const lower = heading.toLowerCase();
  if (lower.includes('summary') || lower.includes('key point') || lower.includes('takeaway') ||
      lower.includes('bottom line') || lower.includes('overview')) {
    return 'summary';
  }
  if (lower.includes('clinical rationale') || lower.includes('rationale') ||
      lower.includes('pathophysiol') || lower.includes('mechanism')) {
    return 'rationale';
  }
  if (lower.includes('detail') || lower.includes('explanation') || lower.includes('analysis') ||
      lower.includes('discussion') || lower.includes('elaboration')) {
    return 'explanation';
  }
  if (lower.includes('clinical consideration') || lower.includes('consideration') ||
      lower.includes('patient') || lower.includes('follow-up') || lower.includes('follow up') ||
      lower.includes('monitoring') || lower.includes('prognosis')) {
    return 'considerations';
  }
  if (lower.includes('flag') || lower.includes('warning') || lower.includes('caution') ||
      lower.includes('contraindic') || lower.includes('adverse') || lower.includes('side effect') ||
      lower.includes('alert') || lower.includes('precaution')) {
    return 'flags';
  }
  if (lower.includes('protocol') || lower.includes('step') || lower.includes('procedure') ||
      lower.includes('algorithm') || lower.includes('workflow')) {
    return 'protocol';
  }
  if (lower.includes('treatment') || lower.includes('therapy') || lower.includes('medication') ||
      lower.includes('dosage') || lower.includes('drug') || lower.includes('pharmacol')) {
    return 'treatment';
  }
  if (lower.includes('diagnosis') || lower.includes('diagnostic') || lower.includes('workup') ||
      lower.includes('test') || lower.includes('lab') || lower.includes('imaging')) {
    return 'diagnosis';
  }
  if (lower.includes('evidence') || lower.includes('study') || lower.includes('trial') ||
      lower.includes('research') || lower.includes('guideline') || lower.includes('literature')) {
    return 'evidence';
  }
  if (lower.includes('risk') || lower.includes('complication') || lower.includes('outcome')) {
    return 'risk';
  }
  if (lower.includes('action') || lower.includes('recommendation') || lower.includes('conclusion') ||
      lower.includes('introduction')) {
    return 'action';
  }
  if (lower.includes('epidemiol') || lower.includes('prevalence') || lower.includes('incidence') ||
      lower.includes('demographic')) {
    return 'epidemiology';
  }
  if (lower.includes('anatomy') || lower.includes('physiolog') || lower.includes('structure')) {
    return 'anatomy';
  }
  if (lower.includes('differential') || lower.includes('differentiating')) {
    return 'differential';
  }
  return 'general';
};

const getSectionIcon = (type) => {
  const icons = {
    rationale:      'fa-solid fa-briefcase-medical',    // clinical rationale
    explanation:    'fa-solid fa-file-waveform',         // detailed explanation / analysis
    considerations: 'fa-solid fa-heart-pulse',           // clinical considerations / monitoring
    summary:        'fa-solid fa-clipboard-list',        // summary / key points
    flags:          'fa-solid fa-triangle-exclamation',   // warnings / contraindications
    protocol:       'fa-solid fa-list-check',            // protocols / procedures / steps
    treatment:      'fa-solid fa-pills',                 // treatment / therapy / medication
    diagnosis:      'fa-solid fa-stethoscope',           // diagnosis / workup / testing
    evidence:       'fa-solid fa-book-medical',          // evidence / studies / guidelines
    risk:           'fa-solid fa-shield-virus',           // risks / complications
    action:         'fa-solid fa-clipboard-check',       // recommendations / conclusions
    epidemiology:   'fa-solid fa-chart-pie',             // epidemiology / prevalence
    anatomy:        'fa-solid fa-lungs',                 // anatomy / physiology
    differential:   'fa-solid fa-code-compare',          // differential diagnosis
    general:        'fa-solid fa-notes-medical',         // fallback medical icon
  };
  return icons[type] || icons.general;
};

const getSectionColor = (type) => {
  // All section icons use DM Red per Figma design
  return '#CB2323';
};

/**
 * Convert ALL-CAPS text to sentence case for readability.
 * Preserves short words and medical acronyms (CDI, NAAT, PCR, CTLA-4, etc.).
 */
const toSentenceCase = (str) => {
  // Only convert long headings (>10 words) — short labels like "TREATMENT" are fine in caps
  const wordCount = str.trim().split(/\s+/).length;
  if (wordCount <= 10) return str;
  const letters = str.replace(/[^a-zA-Z]/g, '');
  if (!letters || (letters.replace(/[A-Z]/g, '').length / letters.length) > 0.4) return str;
  return str.split(/(\s+|[,;:()\[\]\/])/).map((word, i, arr) => {
    if (word.length <= 5 && word === word.toUpperCase() && /[A-Z]/.test(word)) return word;
    if (/\d/.test(word)) return word;
    if (word.includes('-') && word.length <= 10) return word;
    const prevWords = arr.slice(0, i).filter(w => /[a-zA-Z]/.test(w));
    if (prevWords.length === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word.toLowerCase();
  }).join('');
};

/**
 * Parse answer text into sections based on markdown headings.
 * Returns array of { heading, content, type }.
 */
const parseAnswerSections = (text) => {
  if (!text) return [];

  const lines = text.split('\n');
  const sections = [];
  let currentSection = null;
  const headingRegex = /^(#{1,3})\s+(.+)$/;
  const numberedHeadingRegex = /^(?:\*\*)?(\d+)\.\s*(.+?)(?:\*\*)?$/;

  for (const line of lines) {
    const headingMatch = line.match(headingRegex);
    const numberedMatch = !headingMatch ? line.match(numberedHeadingRegex) : null;

    if (headingMatch || numberedMatch) {
      if (currentSection) {
        currentSection.content = currentSection.content.trim();
        sections.push(currentSection);
      }
      const rawHeading = headingMatch
        ? headingMatch[2].replace(/^\d+\.\s*/, '').trim()
        : numberedMatch[2].replace(/\*\*/g, '').trim();

      // If the "heading" contains substantial inline content after a colon
      // (e.g. "## Recommendation: Full paragraph here..."), split it so only
      // the label before the colon becomes the heading and the rest becomes content.
      let headingTitle = rawHeading;
      let spillContent = '';
      const colonIdx = rawHeading.search(/:\s+\S/);
      if (colonIdx !== -1) {
        const afterColon = rawHeading.slice(colonIdx + 1).trim();
        // Only split if what follows the colon is substantial paragraph content (>60 chars)
        if (afterColon.length > 60) {
          headingTitle = rawHeading.slice(0, colonIdx).replace(/\*\*/g, '').trim();
          spillContent = afterColon;
        }
      }
      // Also guard against the entire matched text being a long prose line (no real title structure)
      if (headingTitle.length > 120) {
        // Treat as plain content, not a heading
        if (currentSection) {
          currentSection.content += line + '\n';
        } else {
          currentSection = { heading: 'Summary', content: line + '\n', type: 'action' };
        }
        continue;
      }

      const headingText = toSentenceCase(headingTitle);
      currentSection = {
        heading: headingText,
        content: spillContent ? spillContent + '\n' : '',
        type: detectSectionType(headingText),
      };
    } else {
      if (currentSection) {
        currentSection.content += line + '\n';
      } else if (line.trim()) {
        currentSection = {
          heading: 'Summary',
          content: line + '\n',
          type: 'action',
        };
      }
    }
  }

  if (currentSection) {
    currentSection.content = currentSection.content.trim();
    sections.push(currentSection);
  }

  return sections;
};

function SearchBar({ onHideBanner, onResultsMode }) {
  const { user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [showSearchGate, setShowSearchGate] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState({});
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [randomAnswer, setRandomAnswer] = useState(null);
  const [expandedAnswers, setExpandedAnswers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchBarForceShow, setSearchBarForceShow] = useState(true);
  const [showContextFrame, setShowContextFrame] = useState(false);
  const [relatedQuestion, setRelatedQuestion] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState(null);
  const [searchCache, setSearchCache] = useState({});
  const [displayedQuery, setDisplayedQuery] = useState("");
  const [qaHistory, setQaHistory] = useState([]);
  const mostSearchedQueries = React.useMemo(() => {
  const frequencyMap = {};

  searchHistory.forEach((q) => {
    const key = q.toLowerCase().trim();
    frequencyMap[key] = (frequencyMap[key] || 0) + 1;
  });

  return Object.entries(frequencyMap)
    .sort((a, b) => b[1] - a[1])   // sort by count desc
    .slice(0, 5)                  // top 5
    .map(([query]) => query);
}, [searchHistory]);

  const handleThreadSelect = (messages, searches, savedThreadContext) => {
  const newThreadId = sessionStorage.getItem("currentThreadId");
  activeThreadIdRef.current = newThreadId;

  // Read latest from sessionStorage (includes background stream updates)
  const threads = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
  const saved = threads.find(t => t.id == newThreadId);

  // Check if this thread has an active background stream
  const bgStream = backgroundStreamsRef.current[newThreadId];
  if (bgStream) {
    // Thread is still streaming — show its current progress
    setQaHistory([...bgStream.localHistory]);
    setLoading(true);
  } else {
    // Thread is idle — load from sessionStorage
    setQaHistory(saved?.qaHistory || messages);
    setLoading(false);
  }

  setSearchHistory(saved?.searches || searches);
  setThreadContext(saved?.threadContext || savedThreadContext || null);
  setFirstQuerySubmitted((bgStream ? bgStream.localHistory : (saved?.qaHistory || messages)).length > 0);
};
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);
  const [firstQuerySubmitted, setFirstQuerySubmitted] = useState(false);
  useEffect(() => {
    if (firstQuerySubmitted) {
      onHideBanner?.();
      onResultsMode?.();
    }
  }, [firstQuerySubmitted]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pageRefreshed, setPageRefreshed] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isDeepThinking, setIsDeepThinking] = useState(false);
  // Gemini is now the default LLM provider for /api/query/stream. The backend
  // also defaults use_gemini to true server-side, but we set it explicitly here
  // so the intent is visible at the call site and so an older backend (where
  // the default was false) still hits Gemini if rolled back transiently.
  // The toggle is currently threaded through to Sidebar but no UI surfaces it.
  const [useGemini, setUseGemini] = useState(true);
  const qaHistoryRef = React.useRef(null);
  const qaContentRef = React.useRef(null);
  const scrollRafRef = React.useRef(null);
  const userScrolledUpRef = React.useRef(false);
  const initialScrollDoneRef = React.useRef(false);
  const programmaticScrollRef = React.useRef(false);
  const loadingRef = React.useRef(false);
  const scrollListenerRef = React.useRef(null);
  const tokenBufferRef = React.useRef({ pending: false, answer: '' });
  const sessionSaveTimerRef = React.useRef(null);
  const [citationPopup, setCitationPopup] = useState({ show: false, citations: [], position: { x: 0, y: 0 } });
  const [multimodalModal, setMultimodalModal] = useState({ show: false, citation: null });
  const [threadContext, setThreadContext] = useState(null);

  // Refs for background streaming support
  const activeThreadIdRef = React.useRef(sessionStorage.getItem("currentThreadId") || null);
  const backgroundStreamsRef = React.useRef({});
  const requestIdRef = React.useRef(null);

  useEffect(() => {
  const savedThreads = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
  const savedActiveThreadId = sessionStorage.getItem("currentThreadId");

  let activeThread = null;

  if (savedActiveThreadId) {
    activeThread = savedThreads.find(t => t.id == savedActiveThreadId);
  }

  if (!activeThread && savedThreads.length > 0) {
    activeThread = savedThreads[0];
    sessionStorage.setItem("currentThreadId", activeThread.id);
  }

  if (activeThread) {
    setQaHistory(activeThread.qaHistory || []);
    setSearchHistory(activeThread.searches || []);
    setThreadContext(activeThread.threadContext || null);
    if (activeThread.qaHistory.length > 0) {
      setFirstQuerySubmitted(true);
    }
  }
}, []);
  useEffect(() => {
  const savedMostSearched =
    JSON.parse(localStorage.getItem("mostSearched")) || [];

  // Only preload if user has not searched in this session
  if (savedMostSearched.length > 0 && searchHistory.length === 0) {
    setSearchHistory(savedMostSearched);
  }
}, []);

  useEffect(() => {
  if (searchHistory.length === 0) return;

  const counts = {};
  searchHistory.forEach(q => {
    counts[q] = (counts[q] || 0) + 1;
  });

  const sorted = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([query]) => query)
    .slice(0, 5);

  localStorage.setItem("mostSearched", JSON.stringify(sorted));
}, [searchHistory]);

  useEffect(() => {
    // Debounce sessionStorage writes to avoid JSON.stringify on every token frame
    clearTimeout(sessionSaveTimerRef.current);
    sessionSaveTimerRef.current = setTimeout(() => {
      sessionStorage.setItem("qaHistory", JSON.stringify(qaHistory));
      sessionStorage.setItem("searchHistory", JSON.stringify(searchHistory));
    }, 300);

    // Auto-scroll to bottom while streaming — skip if user manually scrolled up
    // Skip when not loading so scroll-to-answer-top (in QAItem) isn't overridden
    // Skip until initial scroll positioning (question at top) is complete
    if (qaHistoryRef.current && qaHistory.length > 0 && loading && !userScrolledUpRef.current && initialScrollDoneRef.current) {
      if (!scrollRafRef.current) {
        scrollRafRef.current = requestAnimationFrame(() => {
          const el = qaHistoryRef.current;
          if (el) {
            const lastQa = qaHistory[qaHistory.length - 1];
            const isThinking = lastQa?.answer === "loading";
            const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            // During thinking phase: always follow (steps grow below the question)
            // During token streaming: only nudge if near bottom (user may be reading)
            if (isThinking || distFromBottom < 150) {
              programmaticScrollRef.current = true;
              el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
              setTimeout(() => { programmaticScrollRef.current = false; }, 200);
            }
          }
          scrollRafRef.current = null;
        });
      }
    }

  }, [qaHistory, searchHistory, loading]);

  // Detect manual scroll-up to disengage auto-scroll during streaming
  loadingRef.current = loading;
  useEffect(() => {
    const el = qaHistoryRef.current;
    if (!el || scrollListenerRef.current === el) return;
    // Clean up previous listener if element changed
    if (scrollListenerRef.current) {
      scrollListenerRef.current.removeEventListener('scroll', scrollListenerRef.current._scrollHandler);
    }
    const handleScroll = () => {
      if (!loadingRef.current) return;
      // Skip scroll events caused by our own programmatic scrolling
      if (programmaticScrollRef.current) return;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // User is far from bottom — they scrolled up intentionally
      if (distFromBottom > 300) {
        userScrolledUpRef.current = true;
      }
      // User scrolled back near bottom — re-engage auto-scroll
      if (distFromBottom < 100) {
        userScrolledUpRef.current = false;
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    el._scrollHandler = handleScroll;
    scrollListenerRef.current = el;
    return () => el.removeEventListener('scroll', handleScroll);
  }, [qaHistory.length > 0]); // Re-run when qa-history div mounts/unmounts

  // When user logs in, reset the free search counter and close the gate
  useEffect(() => {
    if (isAuthenticated) {
      clearFreeSearchCount();
      setShowSearchGate(false);
    }
  }, [isAuthenticated]);

  const handleFeedbackSubmit = (feedback) => {
    if (feedback.trim()) {
      setSubmittedFeedback(feedback);
      console.log("Feedback submitted:", feedback);
      setShowFeedbackPopup(false);
    }
  };

  const handleRelatedQuestion = (questionData) => {
    setQuery(questionData.input);
    setRelatedQuestion(questionData);
    setRandomAnswer(questionData.answers[0]);
  };
  
const handleSearch = (customQuery, enableQueryRewrite = true) => {
  const searchQuery = customQuery || query;
  if (!searchQuery.trim()) return;

  // Gate: unauthenticated users are limited to FREE_SEARCH_LIMIT searches
  if (!isAuthenticated) {
    if (hasReachedFreeLimit()) {
      setShowSearchGate(true);
      return;
    }
    incrementFreeSearchCount();
  }

  // 🔥 Move search bar to bottom immediately when searching
  setFirstQuerySubmitted(true);
  setShowSuggestions(false);
  setLoading(true);  // Start loading state
  setSearchBarForceShow(false); // Hide search bar during streaming
  userScrolledUpRef.current = false; // Reset scroll tracking for new search
  initialScrollDoneRef.current = false; // Block auto-scroll until initial position is set

    // Use current thread id from sessionStorage (created and managed in Sidebar)
    const currentThreadId =
      sessionStorage.getItem("currentThreadId") || "default";
    const streamThreadId = currentThreadId;
    activeThreadIdRef.current = streamThreadId;

    // Add new question at the end of history (chronological order)
    const startTime = Date.now();

    const newQaItem = {
      question: searchQuery,
      answer: "loading",
      time: null,
      startTime: startTime,
      thinkingPhase: "searching",
      thinkingSteps: [],
    };

    // Build local history for background streaming support
    const localHistory = [...qaHistory, newQaItem];
    setQaHistory(localHistory);
    backgroundStreamsRef.current[streamThreadId] = {
      localHistory: localHistory.map(item => ({ ...item })),
    };

    setDisplayedQuery(searchQuery);
    setQuery("");

    // Scroll so the new question + thinking block is visible at the bottom of the view
    // Retry until the new QA item is in the DOM (needed on mobile where render can lag)
    const scrollToLatest = (retries = 0) => {
      requestAnimationFrame(() => {
        if (qaHistoryRef.current) {
          const el = qaHistoryRef.current;
          const qaItems = qaContentRef.current?.querySelectorAll('.qa-item');
          const lastItem = qaItems?.[qaItems.length - 1];
          if (lastItem) {
            // Scroll the qa-history container to the bottom so the new question is visible
            // without pushing previous content behind the topbar
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            setTimeout(() => { initialScrollDoneRef.current = true; }, 400);
            return;
          }
        }
        // Retry up to 5 times (~300ms total) if new item isn't rendered yet
        if (retries < 5) {
          setTimeout(() => scrollToLatest(retries + 1), 60);
        } else {
          initialScrollDoneRef.current = true;
        }
      });
    };
    scrollToLatest();

    // Use streaming API for query
    const queryStream = async () => {
      const apiEndpoint = "/api/query/stream";

      // Background streaming helpers
      const stream = backgroundStreamsRef.current[streamThreadId];
      const isActive = () => activeThreadIdRef.current == streamThreadId && backgroundStreamsRef.current[streamThreadId] === stream;
      const updateLastQaItem = (updater) => {
        if (!stream) return;
        const lastIdx = stream.localHistory.length - 1;
        stream.localHistory[lastIdx] = updater(stream.localHistory[lastIdx]);
        if (isActive()) setQaHistory([...stream.localHistory]);
      };
      const persistQaToSession = () => {
        if (!stream) return;
        const threads = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
        const idx = threads.findIndex(t => t.id == streamThreadId);
        if (idx >= 0) {
          threads[idx].qaHistory = stream.localHistory;
          sessionStorage.setItem("chatThreads", JSON.stringify(threads));
        }
      };

      // Abort controller with inactivity timeout — if no data arrives for 60s, abort
      // Inactivity timeout — aborts the fetch if no data arrives for this
      // long. Must be at least as large as the backend's STREAM_TIMEOUT_SECONDS
      // (default 120s) so a slow-but-progressing query isn't killed client-side
      // before it can complete. Long thread-context follow-ups can hit ~60s
      // total; we set this to 120s to give comfortable headroom.
      const INACTIVITY_TIMEOUT_MS = 120000;
      const abortController = new AbortController();
      let inactivityTimer = setTimeout(() => abortController.abort(), INACTIVITY_TIMEOUT_MS);
      const resetInactivityTimer = () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => abortController.abort(), INACTIVITY_TIMEOUT_MS);
      };

      // --- SSE event processor (shared by SW-proxied and direct-fetch paths) ---
      let fullAnswer = '';
      let citationMap = {};
      let references = [];
      let followupQuestions = [];

      const processSSEEvent = (eventType, data) => {
        switch (eventType) {
          case 'thinking':
            if (data.request_id) {
              requestIdRef.current = data.request_id;
              // Note: previously this block auto-fired POST /answer-now when
              // isDeepThinking was off. That request raced with the stream's
              // own completion — by the time the POST arrived, the backend
              // had already cleaned up the request_id registry, producing a
              // harmless-but-noisy 404 in DevTools. The stream is already as
              // fast as it can be; if the user truly wants to skip phases
              // they can click the manual "Answer Now" button (handled
              // elsewhere via onAnswerNow). Auto-fire removed.
            }
            updateLastQaItem((prev) => {
              const existingSteps = prev.thinkingSteps || [];
              const stepIndex = existingSteps.findIndex(s => s.step === data.step);
              let newSteps;
              if (stepIndex >= 0) {
                newSteps = [...existingSteps];
                newSteps[stepIndex] = { ...newSteps[stepIndex], ...data };
              } else {
                newSteps = [...existingSteps, { ...data }];
              }
              const updates = { ...prev, thinkingPhase: "thinking", thinkingSteps: newSteps };
              if (data.request_id) {
                updates.requestId = data.request_id;
              }
              return updates;
            });
            break;

          case 'metadata':
            updateLastQaItem((prev) => ({
              ...prev,
              thinkingPhase: "analyzing",
              metadataInfo: { numResults: data.num_results, retrievalMode: data.retrieval_mode },
            }));
            break;

          case 'token':
            const text = normalizeCitations(data.text);
            fullAnswer += text;
            tokenBufferRef.current.answer = fullAnswer;
            if (!tokenBufferRef.current.pending) {
              tokenBufferRef.current.pending = true;
              setTimeout(() => {
                const buffered = tokenBufferRef.current.answer;
                tokenBufferRef.current.pending = false;
                updateLastQaItem((prev) => ({ ...prev, answer: buffered, thinkingPhase: null }));
              }, 200);
            }
            break;

          case 'citations':
            console.log('[citations] formatted_citations:', data.formatted_citations?.map(fc => ({ num: fc.citation_number, chunk_type: fc.chunk_type, video_url: fc.video_url, source_link: fc.source_link })));
            console.log('[citations] citation_map sample:', Object.fromEntries(Object.entries(data.citation_map || {}).slice(0, 3)));
            citationMap = data.citation_map || {};
            followupQuestions = data.followup_questions || [];
            if (data.full_answer) {
              fullAnswer = data.full_answer;
            }

            if (data.formatted_citations && data.formatted_citations.length > 0) {
              references = data.formatted_citations.map((fc, idx) => ({
                number: fc.citation_number || (idx + 1),
                title: fc.title || "",
                authors: fc.authors || "",
                journal: fc.organization || fc.specialty || "",
                year: fc.year || "",
                doi: fc.doi || "",
                pubmed_id: fc.pubmed_id || "",
                pubmed_url: fc.pubmed_id ? `https://pubmed.ncbi.nlm.nih.gov/${fc.pubmed_id}/` : "",
                source_link: fc.source_link || "",
                snippet: fc.snippet || "",
                relevance_percentage: fc.relevance_percentage || "",
                display_text: fc.display_text || "",
                specialty: fc.specialty || "",
                organization: fc.organization || "",
                chunk_type: fc.chunk_type || "text",
                visual_type: fc.visual_type || "",
                section_path: fc.section_path || "",
                section_text: fc.section_text || "",
                image_blob_url: fc.image_blob_url || fc.image_url || "",
                is_multimodal: fc.is_multimodal || false,
                doc_level: fc.doc_level || "chunk",
                rerank_score: fc.rerank_score || 0,
                enriched_text: fc.enriched_text || "",
                video_url: fc.video_url || "",
                video_blob_url: fc.video_blob_url || "",
                video_title: fc.video_title || "",
                start_time_formatted: fc.start_time_formatted || "",
                end_time_formatted: fc.end_time_formatted || "",
              }));
            } else if (Object.keys(citationMap).length > 0) {
              const cosmosEntries = [];
              const pubmedEntries = [];
              Object.entries(citationMap).forEach(([key, cm]) => {
                const isPub = key.startsWith("PubMed ");
                const origNum = isPub ? parseInt(key.replace('PubMed ', '')) : parseInt(key);
                if (isPub) {
                  pubmedEntries.push({ key, cm, origNum });
                } else {
                  cosmosEntries.push({ key, cm, origNum });
                }
              });
              cosmosEntries.sort((a, b) => a.origNum - b.origNum);
              pubmedEntries.sort((a, b) => a.origNum - b.origNum);

              const allEntries = [...cosmosEntries, ...pubmedEntries];
              const newCitationMap = {};

              references = allEntries
                .map((entry, idx) => {
                  const newNum = idx + 1;
                  newCitationMap[newNum] = entry.cm;
                  return {
                    number: newNum,
                    displayNumber: newNum,
                    isPubMed: entry.key.startsWith("PubMed "),
                    title: entry.cm.title || "",
                    authors: entry.cm.authors || "",
                    journal: entry.cm.journal || entry.cm.specialty || "",
                    year: entry.cm.year || "",
                    doi: (entry.cm.doi || "").toString().trim(),
                    pubmed_id: entry.cm.pubmed_id || "",
                    pubmed_url: entry.cm.pubmed_id ? `https://pubmed.ncbi.nlm.nih.gov/${entry.cm.pubmed_id}/` : (entry.cm.pubmed_url || ""),
                    source_link: entry.cm.source_link || "",
                    snippet: entry.cm.snippet || "",
                    chunk_type: entry.cm.chunk_type || "text",
                    visual_type: entry.cm.visual_type || "",
                    section_path: entry.cm.section_path || "",
                    section_text: entry.cm.section_text || "",
                    image_blob_url: entry.cm.image_blob_url || entry.cm.image_url || "",
                    is_multimodal: entry.cm.is_multimodal || false,
                    doc_level: entry.cm.doc_level || "chunk",
                    rerank_score: entry.cm.rerank_score || 0,
                    enriched_text: entry.cm.enriched_text || "",
                    video_url: entry.cm.video_url || "",
                    video_blob_url: entry.cm.video_blob_url || "",
                    video_title: entry.cm.video_title || "",
                    start_time_formatted: entry.cm.start_time_formatted || "",
                    end_time_formatted: entry.cm.end_time_formatted || "",
                  };
                })
                .filter(ref => ref.title || ref.doi || ref.pubmed_id);

              pubmedEntries.forEach((entry) => {
                const oldPubNum = entry.origNum;
                const newNum = allEntries.indexOf(entry) + 1;
                fullAnswer = fullAnswer.replace(
                  new RegExp(`\\[PubMed\\s+Source\\s+${oldPubNum}(?:[^\\]]*)?\\]`, 'g'),
                  `[${newNum}]`
                );
                fullAnswer = fullAnswer.replace(
                  new RegExp(`\\[PubMed\\s+${oldPubNum}\\]`, 'g'),
                  `[${newNum}]`
                );
              });

              fullAnswer = normalizeCitations(fullAnswer);
              citationMap = newCitationMap;
            } else {
              fullAnswer = normalizeCitations(fullAnswer);
            }

            // Deduplicate references by DOI, remapping duplicate citation numbers to canonical ones
            const { refs: dedupedRefs, remapNumbers } = deduplicateReferencesByDoi(references);
            references = dedupedRefs;
            if (remapNumbers.size) {
              fullAnswer = remapInlineCitations(fullAnswer, remapNumbers);
              remapNumbers.forEach((_, removedNum) => delete citationMap[removedNum]);
            }

            // Rebuild citationMap from the richer formatted_citations data so that inline
            // citation routing (video, multimodal, etc.) has all the same fields as references.
            // Done AFTER dedup so removed numbers are already gone from citationMap.
            if (data.formatted_citations && data.formatted_citations.length > 0) {
              const richMap = {};
              references.forEach(ref => {
                richMap[ref.number] = ref;
              });
              // Preserve any non-numeric (e.g. "PubMed N") entries from the original citationMap.
              Object.entries(citationMap).forEach(([key, val]) => {
                if (isNaN(parseInt(key))) {
                  richMap[key] = val;
                }
              });
              citationMap = richMap;
            }

            if (data.thread_context) {
              const ctx = data.thread_context;
              const newThreadContext = {
                clinicalFrame: ctx.clinical_frame || null,
                clinicalEntities: ctx.clinical_entities || null,
                excludedOptions: ctx.excluded_options || [],
                guidelinePreferences: ctx.guideline_preferences || null,
                conversationTurns: ctx.conversation_turns || 0,
              };
              if (isActive()) setThreadContext(newThreadContext);
              const threads = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
              const idx = threads.findIndex(t => t.id == streamThreadId);
              if (idx >= 0) {
                threads[idx].threadContext = newThreadContext;
                threads[idx].clinicalFrame = ctx.clinical_frame || threads[idx].clinicalFrame;
                sessionStorage.setItem("chatThreads", JSON.stringify(threads));
              }
            }

            // Split references into cited (References panel) vs background
            // (Background sources panel). A reference is "cited" iff its
            // number appears as an inline [N] marker in the final answer text.
            // Refs that lack a number (rare — e.g. PubMed entries that never
            // got a numeric key) are kept in the cited list as a safe default.
            const citedNums = extractCitedNumbers(fullAnswer);
            const citedRefs = [];
            const backgroundRefs = [];
            references.forEach(r => {
              if (r.number == null || citedNums.has(r.number)) {
                citedRefs.push(r);
              } else {
                backgroundRefs.push(r);
              }
            });

            // Renumber cited refs to a clean, contiguous [1..M] sequence and
            // rewrite the answer text to match. This unifies the previously
            // gappy LLM-context numbering (e.g. [7], [10], [PubMed 2]) into
            // [1], [2], [3] that match the References panel labels — standard
            // academic-paper style. citationMap is rekeyed so inline citation
            // clicks still resolve to the same source after renumbering.
            const beforeAnswer = fullAnswer;
            console.log('[renumber] BEFORE', {
              answerMarkers: (fullAnswer.match(/\[(?:PubMed\s+)?\d+(?:[,\s]+\d+)*\]/g) || []).slice(0, 10),
              citedRefCount: citedRefs.length,
              citedRefNumbers: citedRefs.map(r => r.number),
            });
            const renumberOut = renumberRefsByFirstAppearance(citedRefs, fullAnswer);
            console.log('[renumber] AFTER', {
              ranTransform: renumberOut.refs.length > 0,
              renumberedCount: renumberOut.refs.length,
              guidelineRemapEntries: Array.from(renumberOut.remap?.entries() || []),
              pubmedRemapEntries: Array.from(renumberOut.pubMap?.entries() || []),
              newAnswerMarkers: (renumberOut.newAnswer || '').match(/\[(?:PubMed\s+)?\d+(?:[,\s]+\d+)*\]/g)?.slice(0, 10),
            });
            if (renumberOut.refs.length) {
              // Replace citedRefs in place with the renumbered, reordered list
              citedRefs.length = 0;
              renumberOut.refs.forEach(r => citedRefs.push(r));
              fullAnswer = renumberOut.newAnswer;

              // Rekey citationMap so [N] lookups resolve to the new number.
              // Old keys could be ints (1, 2, ...) or "PubMed N" strings.
              const newCitationMap = {};
              renumberOut.remap.forEach((newN, oldN) => {
                const src = citationMap[oldN] ?? citationMap[String(oldN)];
                if (src) newCitationMap[String(newN)] = { ...src, citation_number: newN };
              });
              renumberOut.pubMap.forEach((newN, oldN) => {
                const src = citationMap[`PubMed ${oldN}`];
                if (src) newCitationMap[String(newN)] = { ...src, citation_number: newN };
              });
              // Preserve any non-cited entries (background) under their old
              // keys so background-panel rendering still works.
              Object.entries(citationMap).forEach(([k, v]) => {
                if (!(k in newCitationMap)) newCitationMap[k] = v;
              });
              citationMap = newCitationMap;
            }

            // Background refs get their own clean 1..N (no answer text remap
            // needed — they're never referenced inline by construction).
            backgroundRefs.forEach((r, i) => { r.number = i + 1; });

            updateLastQaItem((prev) => ({
              ...prev,
              answer: fullAnswer,
              references: citedRefs,
              backgroundReferences: backgroundRefs,
              citationMap: citationMap,
              followup_questions: Array.isArray(followupQuestions) ? followupQuestions : [],
            }));
            persistQaToSession();

            if (isActive()) {
              setRandomAnswer({
                answer: fullAnswer,
                citationMap,
                references: citedRefs,
                backgroundReferences: backgroundRefs,
                followup_questions: followupQuestions,
              });
            }
            break;

          case 'answer_now':
            fullAnswer = data.text || '';
            fullAnswer = normalizeCitations(fullAnswer);
            tokenBufferRef.current.answer = fullAnswer;
            updateLastQaItem((prev) => ({
              ...prev,
              answer: fullAnswer,
              thinkingPhase: null,
            }));
            break;

          case 'decision_tree':
          case 'algorithms': {
            // Accept figures from multiple possible field names the API may use
            const dtFigures = data.figures || data.algorithms || data.data || [];
            if (Array.isArray(dtFigures) && dtFigures.length > 0) {
              updateLastQaItem((prev) => ({
                ...prev,
                decisionTreeFigures: dtFigures,
              }));
            }
            break;
          }

          case 'done':
            requestIdRef.current = null;
            updateLastQaItem((prev) => {
              const elapsed = prev.startTime ? ((Date.now() - prev.startTime) / 1000) : null;
              let timeLabel = null;
              if (elapsed !== null) {
                timeLabel = elapsed < 60
                  ? `${elapsed.toFixed(1)}s`
                  : `${Math.floor(elapsed / 60)}m ${Math.round(elapsed % 60)}s`;
              }
              return { ...prev, time: timeLabel };
            });
            if (isActive()) {
              setLoading(false);
              setSearchBarForceShow(true);
            }
            break;

          case 'error':
            console.error('Stream error:', data.error);
            updateLastQaItem((prev) => ({
              ...prev,
              answer: `Error: ${data.error || 'Stream error occurred'}`,
              references: [],
              backgroundReferences: [],
              followup_questions: [],
            }));
            persistQaToSession();
            if (isActive()) {
              setLoading(false);
              setSearchBarForceShow(true);
            }
            break;

          default:
            break;
        }
      };

      // --- Stream data source: SW-proxied or direct fetch ---
      const apiUrl = `${API_URL}${apiEndpoint}`;
      const userHeaders = { 'X-Thread-Id': currentThreadId };
      if (user?.sub) userHeaders['X-User-Id'] = user.sub;
      if (user?.email) userHeaders['X-User-Email'] = user.email;
      const requestBody = {
        question: searchQuery,
        top_k: 10,
        specialty_filter: null,
        thread_id: currentThreadId,
        enable_intent_classification: true,
        enable_query_rewrite: enableQueryRewrite,
        use_agentic: 'auto',
        use_gemini: useGemini,
      };

      try {
        if (isServiceWorkerStreamingSupported()) {
          // ---- Service Worker proxied path (survives mobile tab backgrounding) ----
          await new Promise((resolve, reject) => {
            const handle = createProxiedStream({
              url: apiUrl,
              body: requestBody,
              headers: { 'Content-Type': 'application/json', ...userHeaders },
              onEvent: ({ eventType, data }) => {
                resetInactivityTimer();
                processSSEEvent(eventType, data);
              },
              onDone: () => resolve(fullAnswer),
              onError: (err) => reject(new Error(err)),
            });
            // Forward abort signal to SW
            abortController.signal.addEventListener('abort', () => handle.abort());
          });
        } else {
          // ---- Direct fetch fallback (original behavior) ----
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...userHeaders },
            signal: abortController.signal,
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Stream request failed');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let eventType = null;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            resetInactivityTimer();

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7).trim();
              } else if (line.startsWith('data: ') && eventType) {
                let data;
                try {
                  data = JSON.parse(line.slice(6));
                } catch (e) {
                  console.error('Failed to parse SSE data:', line.slice(6), e);
                  eventType = null;
                  continue;
                }
                processSSEEvent(eventType, data);
                eventType = null;
              }
            }
          }
        }

        return fullAnswer;
      } catch (error) {
        console.error("Stream fetch error:", error);
        const isTimeout = error.name === 'AbortError';
        updateLastQaItem((prev) => ({
          ...prev,
          answer: prev.answer && prev.answer !== "loading"
            ? prev.answer  // Keep partial answer if we have one
            : `Error: ${isTimeout ? 'Request timed out. Please try again.' : (error.message || 'Unable to fetch the answer. Please try again later.')}`,
          references: prev.references || [],
          backgroundReferences: prev.backgroundReferences || [],
          followup_questions: prev.followup_questions || [],
        }));
        persistQaToSession();
        if (isActive()) {
          setLoading(false);
          setSearchBarForceShow(true);
        }
      } finally {
        clearTimeout(inactivityTimer);
        if (isActive()) {
          setLoading(false);
          setSearchBarForceShow(true);
          setShowSuggestions(false);
          setExpandedAnswers(true);
          setSearchHistory((prev) => [...prev, searchQuery]);
          setFirstQuerySubmitted(true);
        }
        // Always persist final state to sessionStorage
        persistQaToSession();
        const threads = JSON.parse(sessionStorage.getItem("chatThreads")) || [];
        const idx = threads.findIndex(t => t.id == streamThreadId);
        if (idx >= 0) {
          const prevSearches = threads[idx].searches || [];
          if (!prevSearches.includes(searchQuery)) {
            threads[idx].searches = [...prevSearches, searchQuery];
          }
          sessionStorage.setItem("chatThreads", JSON.stringify(threads));
        }
        // Clean up background stream tracker (only if this stream is still the active one for this thread)
        if (backgroundStreamsRef.current[streamThreadId] === stream) {
          delete backgroundStreamsRef.current[streamThreadId];
        }
      }
    };

    // Start streaming immediately
    queryStream();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
      // Reset textarea height back to 3 lines
      e.target.style.height = '66px';
    }
  };

  const handleButtonClick = () => {
    handleSearch();
    // Reset textarea height back to 3 lines
    const el = document.getElementById('searchInput');
    if (el) el.style.height = '66px';
  };

  const handleAnswerNow = async () => {
    const rid = requestIdRef.current;
    if (!rid) return;
    // Mark as triggered so UI updates immediately
    setQaHistory((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last && last.answer === "loading") {
        updated[updated.length - 1] = { ...last, answerNowTriggered: true };
      }
      return updated;
    });
    try {
      const headers = { 'Content-Type': 'application/json', 'X-Thread-Id': sessionStorage.getItem("currentThreadId") || '' };
      if (user?.sub) headers['X-User-Id'] = user.sub;
      if (user?.email) headers['X-User-Email'] = user.email;
      await fetch(`${API_URL}/api/query/stream/answer-now`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id: rid }),
      });
    } catch (err) {
      console.error('Answer Now request failed:', err);
    }
  };

  const handleSuggestionClick = (input) => {
    setQuery(input);
    setShowSuggestions(false);
    handleSearch(input);
  };

  const handleHistoryItemClick = (query) => {
    setQuery(query);
    setShowSuggestions(false);
    handleSearch(query);
  };

  const handleNewChatThread = () => {
    activeThreadIdRef.current = sessionStorage.getItem("currentThreadId") || null;
    // Reset state for new chat thread
    setQaHistory([]);
    setSearchHistory([]);
    setQuery("");
    setSelectedObject(null);
    setRandomAnswer(null);
    setExpandedAnswers(false);
    setLoading(false); // ✅ Reset loading state for new thread
    setFirstQuerySubmitted(false);
    setThreadContext(null);
  };

  const handleDeepThinkingChange = (enabled) => {
    setIsDeepThinking(enabled);
  };

  const filteredSuggestions = data.filter((item) => {
    const searchTerm = query.toLowerCase();
    const input = item.input.toLowerCase();
    return searchTerm && input.includes(searchTerm);
  });

  const formatAnswer = (text, citationMap = null) => {
    const getIconForHeading = (heading) => {
      const lower = heading.toLowerCase();
      
      // Treatment & Therapy
      if (lower.includes('treatment') || lower.includes('therapy') || lower.includes('therapeutic')) {
        return 'fa-solid fa-prescription-bottle-medical';
      } else if (lower.includes('medication') || lower.includes('drug') || lower.includes('pharmacol')) {
        return 'fa-solid fa-pills';
      } else if (lower.includes('dosage') || lower.includes('dosing') || lower.includes('administration')) {
        return 'fa-solid fa-syringe';
      } else if (lower.includes('surgery') || lower.includes('surgical') || lower.includes('procedure')) {
        return 'fa-solid fa-user-doctor';
      }
      // Diagnosis & Assessment
      else if (lower.includes('diagnosis') || lower.includes('diagnostic') || lower.includes('workup')) {
        return 'fa-solid fa-microscope';
      } else if (lower.includes('test') || lower.includes('laboratory') || lower.includes('lab ') || lower.includes('biomarker')) {
        return 'fa-solid fa-flask-vial';
      } else if (lower.includes('imaging') || lower.includes('radiol') || lower.includes('scan') || lower.includes('x-ray') || lower.includes('mri') || lower.includes('ct ')) {
        return 'fa-solid fa-x-ray';
      } else if (lower.includes('screening') || lower.includes('detection')) {
        return 'fa-solid fa-magnifying-glass-plus';
      } else if (lower.includes('differential')) {
        return 'fa-solid fa-code-branch';
      } else if (lower.includes('staging') || lower.includes('classification') || lower.includes('grading')) {
        return 'fa-solid fa-layer-group';
      }
      // Symptoms & Signs
      else if (lower.includes('symptom') || lower.includes('sign') || lower.includes('presentation') || lower.includes('manifestation')) {
        return 'fa-solid fa-stethoscope';
      } else if (lower.includes('pain') || lower.includes('discomfort')) {
        return 'fa-solid fa-bolt';
      }
      // Causes & Pathology
      else if (lower.includes('cause') || lower.includes('etiology') || lower.includes('pathogenesis')) {
        return 'fa-solid fa-dna';
      } else if (lower.includes('mechanism') || lower.includes('pathophysiol')) {
        return 'fa-solid fa-gears';
      } else if (lower.includes('genetic') || lower.includes('heredit') || lower.includes('mutation')) {
        return 'fa-solid fa-dna';
      } else if (lower.includes('infection') || lower.includes('bacteria') || lower.includes('virus') || lower.includes('pathogen')) {
        return 'fa-solid fa-virus';
      }
      // Prevention & Safety
      else if (lower.includes('prevention') || lower.includes('prophylaxis') || lower.includes('vaccin')) {
        return 'fa-solid fa-shield-heart';
      } else if (lower.includes('side effect') || lower.includes('adverse') || lower.includes('contraindic') || lower.includes('toxicity')) {
        return 'fa-solid fa-circle-exclamation';
      } else if (lower.includes('interaction') || lower.includes('precaution') || lower.includes('warning')) {
        return 'fa-solid fa-triangle-exclamation';
      }
      // Prognosis & Outcomes
      else if (lower.includes('prognosis') || lower.includes('outcome') || lower.includes('survival')) {
        return 'fa-solid fa-chart-line';
      } else if (lower.includes('recovery') || lower.includes('rehabilitation') || lower.includes('rehab')) {
        return 'fa-solid fa-heart-pulse';
      } else if (lower.includes('mortality') || lower.includes('death rate') || lower.includes('fatality')) {
        return 'fa-solid fa-chart-column';
      }
      // Risk & Epidemiology
      else if (lower.includes('risk factor') || lower.includes('risk ')) {
        return 'fa-solid fa-gauge-high';
      } else if (lower.includes('complication') || lower.includes('sequelae')) {
        return 'fa-solid fa-link-slash';
      } else if (lower.includes('epidemiol') || lower.includes('prevalence') || lower.includes('incidence')) {
        return 'fa-solid fa-chart-pie';
      }
      // Guidelines & Recommendations
      else if (lower.includes('recommendation') || lower.includes('guideline') || lower.includes('protocol') || lower.includes('standard')) {
        return 'fa-solid fa-clipboard-check';
      } else if (lower.includes('evidence') || lower.includes('study') || lower.includes('trial') || lower.includes('research')) {
        return 'fa-solid fa-book-medical';
      }
      // Anatomy & Body Systems
      else if (lower.includes('anatomy') || lower.includes('structure') || lower.includes('morphol')) {
        return 'fa-solid fa-lungs';
      } else if (lower.includes('cardiac') || lower.includes('heart') || lower.includes('cardiovasc')) {
        return 'fa-solid fa-heart';
      } else if (lower.includes('brain') || lower.includes('neuro') || lower.includes('cerebr')) {
        return 'fa-solid fa-brain';
      } else if (lower.includes('liver') || lower.includes('hepat')) {
        return 'fa-solid fa-droplet';
      } else if (lower.includes('kidney') || lower.includes('renal')) {
        return 'fa-solid fa-filter';
      } else if (lower.includes('bone') || lower.includes('musculoskel') || lower.includes('orthop')) {
        return 'fa-solid fa-bone';
      } else if (lower.includes('eye') || lower.includes('vision') || lower.includes('ophthal')) {
        return 'fa-solid fa-eye';
      } else if (lower.includes('skin') || lower.includes('dermat')) {
        return 'fa-solid fa-hand-dots';
      }
      // Patient & Lifestyle
      else if (lower.includes('diet') || lower.includes('nutrition') || lower.includes('food')) {
        return 'fa-solid fa-apple-whole';
      } else if (lower.includes('exercise') || lower.includes('physical activity') || lower.includes('fitness')) {
        return 'fa-solid fa-person-running';
      } else if (lower.includes('lifestyle') || lower.includes('self-care') || lower.includes('management')) {
        return 'fa-solid fa-hand-holding-heart';
      } else if (lower.includes('mental') || lower.includes('psych') || lower.includes('anxiety') || lower.includes('depress')) {
        return 'fa-solid fa-brain';
      } else if (lower.includes('pediatr') || lower.includes('child') || lower.includes('infant') || lower.includes('neonat')) {
        return 'fa-solid fa-baby';
      } else if (lower.includes('pregnan') || lower.includes('maternal') || lower.includes('obstet')) {
        return 'fa-solid fa-person-pregnant';
      } else if (lower.includes('elder') || lower.includes('geriatr') || lower.includes('aging')) {
        return 'fa-solid fa-person-cane';
      }
      // General / Info
      else if (lower.includes('overview') || lower.includes('introduction') || lower.includes('summary') || lower.includes('background')) {
        return 'fa-solid fa-circle-info';
      } else if (lower.includes('definition') || lower.includes('what is') || lower.includes('about')) {
        return 'fa-solid fa-book-open';
      } else if (lower.includes('type') || lower.includes('categor') || lower.includes('form ') || lower.includes('variant')) {
        return 'fa-solid fa-sitemap';
      } else if (lower.includes('comparison') || lower.includes('versus') || lower.includes(' vs ') || lower.includes('difference')) {
        return 'fa-solid fa-scale-balanced';
      } else if (lower.includes('monitoring') || lower.includes('follow-up') || lower.includes('follow up') || lower.includes('surveillance')) {
        return 'fa-solid fa-chart-area';
      } else if (lower.includes('emergency') || lower.includes('urgent') || lower.includes('acute')) {
        return 'fa-solid fa-truck-medical';
      } else if (lower.includes('conclusion') || lower.includes('key point') || lower.includes('takeaway') || lower.includes('bottom line')) {
        return 'fa-solid fa-flag-checkered';
      } else if (lower.includes('note') || lower.includes('important') || lower.includes('caveat')) {
        return 'fa-solid fa-note-sticky';
      } else if (lower.includes('reference') || lower.includes('source') || lower.includes('citation')) {
        return 'fa-solid fa-quote-left';
      }
      
      return 'fa-solid fa-arrow-right';
    };
    
    // Safety net: normalize any remaining [Source N] citations missed during streaming
    text = normalizeCitations(text);
    let result = text
        // Replace bullet lines with arrow-indented sub-points
        // Matches: - **Heading:** body text (single line only — no cross-line capture)
        .replace(/^\s*[-•]\s*\*\*(.*?)\*\*:?\s*(.*)$/gm,
          (match, heading, body) => {
            const trimmedBody = body.trim();
            const bodyHtml = trimmedBody ? ` ${trimmedBody}` : '';
            return `<div class="arrow-bullet"><span class="arrow-bullet-prefix">→</span><div class="arrow-bullet-content"><strong>${heading.trim().replace(/:$/, '')}:</strong>${bodyHtml}</div></div>`;
          })
        // Convert remaining plain dash/bullet lines to arrow bullets (without bold heading)
        // Allows optional leading whitespace for indented sub-bullets
        .replace(/^\s*[-•]\s+(.+)$/gm,
          (match, content) => {
            if (content.includes('arrow-bullet')) return match;
            return `<div class="arrow-bullet"><span class="arrow-bullet-prefix">→</span><div class="arrow-bullet-content">${content.trim()}</div></div>`;
          })
        // Strip remaining **bold** markers (arrow-bullet headings already handled above)
        .replace(/\*\*(.*?)\*\*/g, "$1")
        // Strip remaining *italic* markers
        .replace(/\*(.*?)\*/g, "$1")
        // Replace ### Heading with <h3> and icon — sentence case for readability
        .replace(/^### (.*?)$/gm, (match, heading) => {
          const icon = getIconForHeading(heading);
          return `<h3><i class="${icon}"></i> ${toSentenceCase(heading)}</h3>`;
        })
        // Replace ## Heading with <h2> and icon
        .replace(/^## (.*?)$/gm, (match, heading) => {
          const icon = getIconForHeading(heading);
          return `<h2><i class="${icon}"></i> ${toSentenceCase(heading)}</h2>`;
        })
        // Replace # Heading with <h1> and icon
        .replace(/^# (.*?)$/gm, (match, heading) => {
          const icon = getIconForHeading(heading);
          return `<h1><i class="${icon}"></i> ${toSentenceCase(heading)}</h1>`;
        })
        // Add visual breaks before numbered subsections (e.g., 1) ... 2) ...)
        .replace(/\n(\d+)\)\s+/g, '\n\n<strong class="numbered-point">$1)</strong> ')
        // Convert --- horizontal rules to <hr>
        .replace(/^-{3,}\s*$/gm, '<hr>')
        // Strip newlines between consecutive arrow-bullet divs (they become unwanted <br> gaps)
        .replace(/<\/div>\n+(<div class="arrow-bullet">)/g, '</div>$1')
        // Replace remaining newlines with <br>
        .replace(/\n/g, "<br>");

    // Embed inline citation links — if citationMap available, make clickable; otherwise render as styled badges
    // Handle numeric [N], [N, M], and [N-M] range citations
    // Ranges render as a single compact badge; individual numbers render separately
    result = result.replace(
      /\[(\d[\d,\s\-\u2013]*\d|\d+)\]/g,
      (match, numbers) => {
        const nums = expandCitationRanges(numbers);
        const isRange = nums.length > 3;

        // For ranges (>3 citations), always render one compact grouped badge
        if (isRange) {
          if (citationMap && Object.keys(citationMap).length > 0) {
            const resolvedNums = nums.filter(n => citationMap[parseInt(n)] || citationMap[n]);
            if (resolvedNums.length > 0) {
              return `<a href="#" class="citation-link citation-range" data-citations="${resolvedNums.join(',')}" title="${resolvedNums.length} references — click to view">[${numbers}]</a>`;
            }
          }
          // During streaming (no citationMap yet), keep compact display
          return `<span class="citation-badge citation-range">[${numbers}]</span>`;
        }

        // For small groups (<=3), render individual links
        const links = nums.map(num => {
          if (citationMap && Object.keys(citationMap).length > 0) {
            const ref = citationMap[parseInt(num)] || citationMap[num];
            if (ref) {
              let href = '#';
              if (ref.source_link) {
                href = ref.source_link;
              } else if (ref.pubmed_url) {
                href = ref.pubmed_url;
              } else if (ref.doi) {
                href = `https://doi.org/${ref.doi}`;
              } else if (ref.video_url) {
                href = ref.video_url;
              } else if (ref.filename) {
                href = `/documents/${ref.filename}${ref.chunk_preview ? '#chunk-' + ref.chunk_preview : ''}`;
              }
              const isVideoRef = ref.chunk_type === 'video_transcript' || ref.chunk_type === 'video_keyframe' || ref.chunk_type === 'video_slide';
              const typeIcon = ref.chunk_type === 'table' ? '📊 ' : ref.chunk_type === 'figure_description' ? '🖼️ ' : isVideoRef ? '🎬 ' : '';
              const title = `${typeIcon}${ref.title || 'Reference'} ${ref.year ? `(${ref.year})` : ''}`.trim();
              const multimodalClass = (ref.is_multimodal || isVideoRef) ? ' citation-multimodal' : '';
              return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="citation-link${multimodalClass}" data-citation="${num}" title="${title}">[${num}]</a>`;
            }
          }
          return `<span class="citation-badge" data-citation="${num}">${num}</span>`;
        });
        return links.join('');
      }
    );

    return result;
  };

  const handleCopy = (answer, index) => {
    navigator.clipboard.writeText(answer);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Render markdown text with citation links
  const renderMarkdownWithCitationLinks = (text, citationMap) => {
    if (!text || !citationMap) return text;
    
    // Handle numeric [N] citations
    let result = text.replace(/\[(\d+)\]/g, (match, num) => {
      const ref = citationMap[parseInt(num)] || citationMap[num];
      if (ref) {
        let href = '#';
        if (ref.source_link) {
          href = ref.source_link;
        } else if (ref.pubmed_url) {
          href = ref.pubmed_url;
        } else if (ref.doi) {
          href = `https://doi.org/${ref.doi}`;
        } else if (ref.video_url) {
          href = ref.video_url;
        } else if (ref.filename) {
          href = `/documents/${ref.filename}${ref.chunk_preview ? '#chunk-' + ref.chunk_preview : ''}`;
        }
        const title = `${ref.title || 'Reference'} ${ref.year ? `(${ref.year})` : ''}`.trim();
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="citation-link" data-citation="${num}" title="${title}">[${num}]</a>`;
      }
      return match;
    });

    return result;
  };

  // Make [N] and [PubMed N] citations clickable and show citation details
  const makeInlineCitationsInteractive = (answerElement, citationMap) => {
    if (!answerElement || !citationMap || Object.keys(citationMap).length === 0) return;

    // Replace [N], [N, M], and [N-M] range patterns with clickable anchor links
    // Ranges (>3) stay as a single compact badge; small groups expand individually
    answerElement.innerHTML = answerElement.innerHTML.replace(
      /\[(\d[\d,\s\-]*\d|\d+)\]/g,
      (match, numbers) => {
        const nums = expandCitationRanges(numbers);
        const isRange = nums.length > 3;

        if (isRange) {
          const resolvedNums = nums.filter(n => citationMap[parseInt(n)] || citationMap[n]);
          if (resolvedNums.length > 0) {
            return `<a href="#" class="citation-link citation-range" data-citations="${resolvedNums.join(',')}" title="${resolvedNums.length} references — click to view" style="cursor: pointer; color: #0066cc; font-weight: 600; text-decoration: none;">[${numbers}]</a>`;
          }
        }

        const links = nums.map(num => {
          const ref = citationMap[parseInt(num)] || citationMap[num];
          if (ref) {
            let href = '#';
            if (ref.pubmed_url) {
              href = ref.pubmed_url;
            } else if (ref.doi) {
              href = `https://doi.org/${ref.doi}`;
            } else if (ref.filename) {
              href = `/documents/${ref.filename}${ref.chunk_preview ? '#chunk-' + ref.chunk_preview : ''}`;
            }
            const title = `${ref.title || 'Reference'} ${ref.year ? `(${ref.year})` : ''}`.trim();
            return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="citation-link" data-citation="${num}" title="${title}" style="cursor: pointer; color: #0066cc; font-weight: 600; text-decoration: none;">[${num}]</a>`;
          }
          return `[${num}]`;
        });
        return links.join('');
      }
    );

    // Add click listeners to citation links for popup (in addition to href navigation)
    answerElement.querySelectorAll('.citation-link').forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href === '#') {
          e.preventDefault();
        }
        
        const citationKey = link.dataset.citation;
        // Look up by string key first (for "PubMed N"), then try integer key
        const citationDetails = citationMap[citationKey] || citationMap[parseInt(citationKey)];
        
        if (citationDetails) {
          e.preventDefault();
          // Route multimodal content to full-screen modal; text-only to small popup
          if (citationDetails.is_multimodal || citationDetails.chunk_type === 'table' || citationDetails.chunk_type === 'figure_description' || citationDetails.image_blob_url || VIDEO_CHUNK_TYPES.has(citationDetails.chunk_type)) {
            setMultimodalModal({ show: true, citation: citationDetails });
          } else {
            const rect = link.getBoundingClientRect();
            setCitationPopup({
              show: true,
              citations: [citationDetails],
              position: { x: rect.left, y: rect.bottom + window.scrollY }
            });
          }
        }
      });
    });
  };

// Simplify technical jargon in thinking step detail text for end users
const simplifyThinkingDetail = (detail) => {
  if (!detail) return detail;
  let text = detail;
  // Remove model names (GPT-*, Claude-*, text-embedding-*, etc.)
  text = text.replace(/\b(GPT[-‑]?\d[\w.-]*|Claude[-‑]?\d[\w.-]*|text[-‑]embedding[-‑][\w.-]+|bge[-‑][\w.-]+|e5[-‑][\w.-]+|all[-‑]MiniLM[\w.-]*)\b/gi, '').trim();
  // Replace technical retrieval / ML terms with plain language
  const replacements = [
    [/cross[-‑\s]?encoder\s*re[-‑]?ranking/gi, 'ranking results by relevance'],
    [/re[-‑]?ranking\s*(with|using|via)\s+[A-Za-z0-9._-]+/gi, 'ranking results by relevance'],
    [/re[-‑]?rank(ing|ed)?\s*(passages|results|chunks|documents)?/gi, 'ranking results by relevance'],
    [/vector\s*(similarity\s*)?search/gi, 'searching knowledge base'],
    [/semantic\s*search/gi, 'searching knowledge base'],
    [/keyword\s*search/gi, 'searching knowledge base'],
    [/hybrid\s*(retrieval|search)/gi, 'searching knowledge base'],
    [/BM25\s*(retrieval|search)?/gi, 'searching knowledge base'],
    [/dense\s*(retrieval|passage\s*retrieval)/gi, 'searching knowledge base'],
    [/embedding(s)?\s*(generation|lookup|search)?/gi, 'searching knowledge base'],
    [/cosine\s*similarity/gi, 'matching relevance'],
    [/chunk(s|ing)?\s*(splitting|overlap)?/gi, 'processing documents'],
    [/token(s|ization|izing)?/gi, 'processing text'],
    [/LLM\s*(call|inference|generation)?/gi, 'analyzing'],
    [/prompt\s*(engineering|construction|assembly|template)?/gi, 'preparing query'],
    [/context\s*window/gi, 'context'],
    [/RAG\s*(pipeline)?/gi, 'retrieval'],
    [/agentic\s*(routing|retrieval|pipeline)?/gi, 'multi-step retrieval'],
    [/comparative\s*=\s*(True|False)/gi, ''],
    [/PubMed/gi, 'medical literature'],
    [/pubmed\s*(search|query|retrieval|lookup)?/gi, 'searching medical literature'],
  ];
  for (const [pattern, replacement] of replacements) {
    text = text.replace(pattern, replacement);
  }
  // Strip parenthetical technical annotations the backend may include, e.g. "(quality: limited)", "(mode: fast)"
  text = text.replace(/\(\s*(quality|mode|tier|level|type)\s*:[^)]*\)/gi, '');
  // Clean up extra whitespace / punctuation artifacts
  text = text.replace(/\s{2,}/g, ' ').replace(/^\s*[,:;]\s*/, '').replace(/\s*[,:;]\s*$/, '').trim();
  return text;
};

// Map thinking step names to appropriate icons
const getThinkingStepIcon = (step) => {
  const iconMap = {
    'intent_classification': 'fa-solid fa-brain',
    'query_rewrite':         'fa-solid fa-pen-to-square',
    'query_expansion':       'fa-solid fa-expand',
    'vector_search':         'fa-solid fa-magnifying-glass',
    'keyword_search':        'fa-solid fa-font',
    'hybrid_search':         'fa-solid fa-layer-group',
    'reranking':             'fa-solid fa-arrow-up-wide-short',
    'sub_query_generation':  'fa-solid fa-code-branch',
    'citation_extraction':   'fa-solid fa-quote-left',
    'agentic_routing':       'fa-solid fa-route',
    'tool_selection':        'fa-solid fa-toolbox',
    'context_assembly':      'fa-solid fa-puzzle-piece',
    'response_generation':   'fa-solid fa-pen-nib',
  };
  return iconMap[step] || 'fa-solid fa-gear';
};


const QAItem = React.memo(function QAItem({
  qa,
  index,
  formatAnswer,
  loadingImage,
  dmilogosmall,
  handleCopy,
  copiedIndex,
  handleSuggestionClick,
  setShowFeedbackPopup,
  setCitationPopup,
  setMultimodalModal,
  isStreaming,
  showContextFrame,
  onToggleContextFrame,
  hasContextFrame,
  threadContext,
  onAnswerNow,
  showAnswerNow,
}) {
  const recommendation = React.useMemo(() => {
    if (!qa.answer || qa.answer === "loading") return "";
    const match =
      qa.answer.match(/Most appropriate recommendation:\s*(.*?)(\.|<br>|$)/i) ||
      qa.answer.match(/Recommended Therapy:\s*(.*?)(\.|<br>|$)/i);
    return match ? match[1].replace(/<[^>]+>/g, "").trim() : "";
  }, [qa.answer]);

  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isQuestionClamped, setIsQuestionClamped] = useState(false);
  const questionTextRef = React.useRef(null);
  useEffect(() => {
    const el = questionTextRef.current;
    if (el) setIsQuestionClamped(el.scrollHeight > el.clientHeight);
  }, [qa.question]);
  const [isAnswerOpen, setIsAnswerOpen] = useState(true);
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const [isBackgroundReferencesOpen, setIsBackgroundReferencesOpen] = useState(false);
  const [isAlgorithmsOpen, setIsAlgorithmsOpen] = useState(true); // open by default — arrives early
  const [isFollowupsOpen, setIsFollowupsOpen] = useState(true);
  const [expandedRefs, setExpandedRefs] = useState({});
  const [expandedBgRefs, setExpandedBgRefs] = useState({});
  // Enrich & Ask: expanded followup card state
  const [enrichCardIndex, setEnrichCardIndex] = useState(null);
  const [enrichContext, setEnrichContext] = useState('');
  const [enrichDecisionInputs, setEnrichDecisionInputs] = useState({});
  const [showAllFollowups, setShowAllFollowups] = useState(false);
  const [enrichFormOpen, setEnrichFormOpen] = useState(false);

  // Seed enrichContext from the clinical frame whenever it changes:
  // - On mount: initializes with the thread's current frame
  // - New turn: updates to the latest accumulated frame (doctor can re-edit)
  // - Thread switch: QAItem remounts with new key, useEffect fires fresh
  React.useEffect(() => {
    const frame = threadContext?.clinicalFrame || '';
    setEnrichContext(frame);
  }, [threadContext?.clinicalFrame]);

  const answerRef = React.useRef(null);
  const answerCardRef = React.useRef(null);
  const qaItemRef = React.useRef(null);
  const wasStreamingRef = React.useRef(false);

  // After streaming completes, scroll the question + answer top into view
  // Skip if answer is still "loading" — means a new question displaced this item,
  // not that streaming actually finished (avoids jitter from competing scrolls)
  React.useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && qaItemRef.current && qa.answer !== "loading") {
      const timer = setTimeout(() => {
        if (qaItemRef.current) {
          qaItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      wasStreamingRef.current = isStreaming;
      return () => clearTimeout(timer);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, qa.answer]);

  // Parse answer into sections for card-based rendering
  // Renders as soon as streaming ends (done event), independently of references
  const answerSections = React.useMemo(() => {
    if (!qa.answer || qa.answer === "loading" || isStreaming) return [];
    return parseAnswerSections(qa.answer);
  }, [qa.answer, isStreaming]);

  const hasSections = answerSections.length > 1;

  // During streaming: lightweight formatter (bold/italic/newlines only, skip icon detection & citation linking)
  // After streaming ends: full formatAnswer with icons, citations, and all rich formatting
  const formattedAnswerHtml = React.useMemo(() => {
    if (!qa.answer || qa.answer === "loading") return "";
    if (isStreaming) {
      return qa.answer
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, "<em>$1</em>")
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>")
        .replace(/\n/g, "<br>");
    }
    return formatAnswer(qa.answer, qa.citationMap);
  }, [qa.answer, qa.citationMap, isStreaming]);


  // Render flag items as colored alert banners
  const renderFlagContent = (content) => {
    const lines = content.split('\n').filter(l => l.trim());
    const items = [];
    let currentText = '';

    for (const line of lines) {
      const bulletMatch = line.trim().match(/^[-•*]\s*(.+)$/);
      if (bulletMatch) {
        if (currentText) {
          items.push({ type: 'text', content: currentText });
          currentText = '';
        }
        const itemText = bulletMatch[1];
        const isUrgent = /urgent|critical|emergency|severe|contraindic|danger|fatal|life.?threaten|immediately/i.test(itemText);
        const isWarning = /warning|caution|monitor|careful|avoid|consider|may|potential|risk/i.test(itemText);
        items.push({
          type: 'flag',
          level: isUrgent ? 'urgent' : isWarning ? 'warning' : 'info',
          content: itemText,
        });
      } else if (line.trim()) {
        currentText += (currentText ? '\n' : '') + line;
      }
    }
    if (currentText) items.push({ type: 'text', content: currentText });

    if (items.filter(i => i.type === 'flag').length === 0) return null;

    return (
      <div className="flag-alerts-container">
        {items.map((item, i) => {
          if (item.type === 'flag') {
            const iconClass = item.level === 'urgent' ? 'fa-circle-exclamation' : item.level === 'warning' ? 'fa-triangle-exclamation' : 'fa-circle-info';
            return (
              <div key={i} className={`flag-alert-item flag-${item.level}`}>
                <i className={`fa-solid ${iconClass}`}></i>
                <span dangerouslySetInnerHTML={{ __html: formatAnswer(item.content, qa.citationMap) }} />
              </div>
            );
          }
          return <div key={i} className="flag-text" dangerouslySetInnerHTML={{ __html: formatAnswer(item.content, qa.citationMap) }} />;
        })}
      </div>
    );
  };

  // Render numbered steps as a visual timeline
  const renderProtocolContent = (content) => {
    const lines = content.split('\n').filter(l => l.trim());
    const steps = [];
    let preamble = '';

    for (const line of lines) {
      const stepMatch = line.trim().match(/^(\d+)[.)]\s*(.+)$/);
      if (stepMatch) {
        steps.push({ num: stepMatch[1], text: stepMatch[2] });
      } else if (steps.length === 0) {
        preamble += (preamble ? '\n' : '') + line;
      } else {
        steps[steps.length - 1].text += ' ' + line.trim();
      }
    }

    if (steps.length < 2) return null;

    return (
      <div className="protocol-timeline-container">
        {preamble && <div className="protocol-preamble" dangerouslySetInnerHTML={{ __html: formatAnswer(preamble, qa.citationMap) }} />}
        <div className="step-timeline">
          {steps.map((step, i) => (
            <div key={i} className="step-timeline-item">
              <div className="step-timeline-marker">
                <div className="step-timeline-dot">{step.num}</div>
                {i < steps.length - 1 && <div className="step-timeline-line" />}
              </div>
              <div className="step-timeline-content" dangerouslySetInnerHTML={{ __html: formatAnswer(step.text, qa.citationMap) }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle citation link clicks via event delegation (survives React re-renders)
  const handleAnswerClick = (e) => {
    const citationLink = e.target.closest('.citation-link');
    if (!citationLink) return;
    e.preventDefault(); // Always prevent navigation — citation links show popups, not URLs

    if (!qa.citationMap) return;

    // Range citations: data-citations="1,2,3,4,5,6" — show all in popup
    const rangeCitations = citationLink.dataset.citations;
    if (rangeCitations) {
      e.preventDefault();
      const keys = rangeCitations.split(',');
      const allDetails = keys
        .map(k => {
          const detail = qa.citationMap[parseInt(k)] || qa.citationMap[k];
          return detail ? { ...detail, _citationNum: k } : null;
        })
        .filter(Boolean);
      if (allDetails.length > 0) {
        const rect = citationLink.getBoundingClientRect();
        setCitationPopup({
          show: true,
          citations: allDetails,
          position: { x: rect.left, y: rect.bottom + window.scrollY }
        });
      }
      return;
    }

    // Single citation: data-citation="5"
    const citationKey = citationLink.dataset.citation;
    const citationDetails = qa.citationMap[citationKey] || qa.citationMap[parseInt(citationKey)];

    if (citationDetails) {
      e.preventDefault();
      if (citationDetails.is_multimodal || citationDetails.chunk_type === 'table' || citationDetails.chunk_type === 'figure_description' || citationDetails.image_blob_url || VIDEO_CHUNK_TYPES.has(citationDetails.chunk_type)) {
        setMultimodalModal({ show: true, citation: citationDetails });
      } else {
        const rect = citationLink.getBoundingClientRect();
        setCitationPopup({
          show: true,
          citations: [citationDetails],
          position: { x: rect.left, y: rect.bottom + window.scrollY }
        });
      }
    }
  };

  return (
    <div className="qa-item" ref={qaItemRef}>

      {/* QUESTION BUBBLE */}
<div className="question-wrapper">

  {qa.time && <span className="timestamp-outside">{qa.time}</span>}

  <div className="question-bubble">
    <p ref={questionTextRef} className={`formatted-query${isQuestionExpanded ? '' : ' question-clamped'}`}>{qa.question}</p>
    {(isQuestionClamped || isQuestionExpanded) && (
      <button
        className="question-toggle-btn"
        onClick={() => setIsQuestionExpanded((prev) => !prev)}
      >
        {isQuestionExpanded ? 'Show less' : 'Show more'}
      </button>
    )}
  </div>

</div>
      {/* THINKING EXPERIENCE */}
      {qa.answer === "loading" && (
        <div className="thinking-block">

          <div className="thinking-header">Thinking...</div>

          {/* Phase sub-text — header stays constant, only this line changes */}
          {(() => {
            // Determine the phase-specific label and icon
            let label, icon;

            if (qa.thinkingPhase === 'analyzing') {
              // Metadata arrived — we know how many sources, whether answer-now or not
              label = `Analyzing ${qa.metadataInfo?.numResults || ''} sources`.trim();
              icon = "fa-solid fa-flask";
            } else if (qa.answerNowTriggered && qa.thinkingSteps?.length > 0) {
              // Answer-now triggered but thinking steps are still arriving — show them
              const latestStep = qa.thinkingSteps[qa.thinkingSteps.length - 1];
              label = simplifyThinkingDetail(latestStep.detail);
              icon = getThinkingStepIcon(latestStep.step);
            } else if (qa.answerNowTriggered) {
              // Answer-now triggered, no new steps yet — show preparing
              label = "Preparing your answer";
              icon = "fa-solid fa-pen-nib";
            } else if (qa.thinkingSteps?.length > 0) {
              // Normal deep-thinking flow
              const latestStep = qa.thinkingSteps[qa.thinkingSteps.length - 1];
              label = simplifyThinkingDetail(latestStep.detail);
              icon = getThinkingStepIcon(latestStep.step);
            } else {
              // Fallback — no events yet
              label = "Searching medical literature";
              icon = "fa-solid fa-magnifying-glass";
            }

            return (
              <div className="thinking-steps">
                <div className="thinking-step latest started">
                  <div className="thinking-step-icon">
                    <i className={`${icon} spinning`}></i>
                  </div>
                  <div className="thinking-step-content">
                    <span className="thinking-step-label">{label}</span>
                    <span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="thinking-progress-bar">
            <div className={`thinking-progress-fill phase-${qa.answerNowTriggered ? 'generating' : (qa.thinkingPhase || 'searching')}`} />
          </div>

          {showAnswerNow && !qa.answerNowTriggered && (
            <button className="answer-now-button" onClick={onAnswerNow}>
              <i className="fa-solid fa-forward"></i>
              Answer Now
            </button>
          )}
        </div>
      )}

      {/* ✅ ONLY ONE WHITE CONTAINER (Answer Card) */}
      {qa.answer !== "loading" && (
        <div className="answer-card" ref={answerCardRef}>

          {/* ANSWER */}
          <div className="accordion-section">
            <div
              className="accordion-header"
              onClick={() => setIsAnswerOpen(!isAnswerOpen)}
            >
              <span className="section-title">
                <img
                  src={dmilogosmall}
                  alt="DeepMedIQ"
                  className="answer-logo"
                />
                Answer
              </span>

              <div className="accordion-header-actions">
                {hasContextFrame && (
                  <button
                    className={`context-frame-toggle ${showContextFrame ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onToggleContextFrame(); }}
                    title={showContextFrame ? "Hide context frame" : "Show context frame"}
                  >
                    <i className="fa-solid fa-brain"></i>
                  </button>
                )}
                <i className={`fa-solid fa-chevron-${isAnswerOpen ? "up" : "down"}`} />
              </div>
            </div>

            {isAnswerOpen && (
              <div className="accordion-body">
                {/* SUMMARY BOX */}
                {recommendation && (
                  <div className="final-answer-box">
                    <div className="final-answer-icon">
                      <i className="fa-solid fa-hand-holding-medical"></i>
                    </div>
                    <div>
                      <div className="final-answer-label">RECOMMENDED THERAPY</div>
                      <div className="final-answer-value">{recommendation}</div>
                    </div>
                  </div>
                )}

                {hasSections ? (
                  <>
                    {/* All sections — flow directly, no primary/detail split */}
                    {answerSections.map((section, idx) => {
                      const sectionColor = getSectionColor(section.type);
                      const sectionIcon = getSectionIcon(section.type);

                      let specialContent = null;
                      if (section.type === 'flags') {
                        specialContent = renderFlagContent(section.content);
                      } else if (section.type === 'protocol') {
                        specialContent = renderProtocolContent(section.content);
                      }

                      return (
                        <div key={idx} className="answer-section-card expanded">
                          <div className="section-card-header">
                            <div className="section-card-title">
                              <i className={sectionIcon} style={{ color: sectionColor }}></i>
                              <span>{section.heading}</span>
                            </div>
                          </div>
                          <div className="section-card-body" onClick={handleAnswerClick}>
                            {specialContent || (
                              <div className="formatted-answer"
                                dangerouslySetInnerHTML={{ __html: formatAnswer(section.content, qa.citationMap) }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div
                    ref={answerRef}
                    className="formatted-answer"
                    onClick={handleAnswerClick}
                    dangerouslySetInnerHTML={{ __html: formattedAnswerHtml }}
                  />
                )}

                {/* FEEDBACK */}
                <div className="feedback-icons">
                  <i
                    className="fa-regular fa-heart"
                    onClick={() => setShowFeedbackPopup(true)}
                  />
                  <i
                    className="fa-solid fa-heart-crack"
                    onClick={() => setShowFeedbackPopup(true)}
                  />
                  {copiedIndex === index ? (
                    <i className="fa-solid fa-check" />
                  ) : (
                    <i
                      className="fa-solid fa-copy"
                      onClick={() => handleCopy(qa.answer, index)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SKELETON LOADERS — only visible while waiting for references (after tokens are streaming) */}
          {isStreaming && !qa.references?.length && qa.answer !== "loading" && qa.answer.length > 100 && (
            <div className="streaming-skeleton">
              <div className="skeleton-section">
                <div className="skeleton-header">
                  <i className="fa-solid fa-book-medical" style={{opacity: 0.4}}></i>
                  <span className="skeleton-title-text">Loading references...</span>
                </div>
                <div className="skeleton-lines">
                  <div className="skeleton-line" style={{width: '90%'}} />
                  <div className="skeleton-line" style={{width: '75%'}} />
                  <div className="skeleton-line" style={{width: '60%'}} />
                </div>
              </div>
            </div>
          )}

          {/* GUIDELINE ALGORITHMS — decision tree figures from decision_tree SSE event */}
          {qa.decisionTreeFigures?.length > 0 && (
            <div className="accordion-section">
              <div
                className="accordion-header"
                onClick={() => setIsAlgorithmsOpen(!isAlgorithmsOpen)}
              >
                <span className="section-title">
                  <i className="fa-solid fa-diagram-project"></i>
                  Guideline Algorithms ({qa.decisionTreeFigures.length})
                </span>
                <i className={`fa-solid fa-chevron-${isAlgorithmsOpen ? "up" : "down"}`} />
              </div>

              {isAlgorithmsOpen && (
                <div className="accordion-body">
                  <div className="dt-strip">
                    {qa.decisionTreeFigures.map((fig, i) => (
                      <div
                        key={fig.image_url + i}
                        className="dt-card"
                        onClick={() => setMultimodalModal({
                          show: true,
                          citation: {
                            title: fig.title,
                            image_blob_url: fig.image_url,
                            source_link: fig.source_link,
                            section_path: fig.section_path,
                            chunk_type: 'figure',
                            visual_type: 'algorithm',
                            journal: fig.source,
                          },
                        })}
                        title="Click to view full algorithm"
                      >
                        <div className="dt-thumb-container">
                          <img
                            src={fig.image_url}
                            alt={fig.title || 'Algorithm'}
                            className="dt-thumb"
                            loading="lazy"
                          />
                          <div className="dt-thumb-overlay">
                            <i className="fa-solid fa-expand"></i>
                          </div>
                        </div>
                        <div className="dt-card-body">
                          <p className="dt-card-title">{fig.title || 'Guideline Algorithm'}</p>
                          <p className="dt-card-source">{fig.source}</p>
                          {fig.relevance_score && (
                            <span className="dt-relevance-badge">
                              {Math.round(fig.relevance_score * 100)}% match
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* REFERENCES — collapsible accordion (inline-cited only) */}
          {qa.references?.length > 0 && (
            <div className="accordion-section">
              <div
                className="accordion-header"
                onClick={() => setIsReferencesOpen(!isReferencesOpen)}
              >
                <span className="section-title">
                  <i className="fa-solid fa-list-ol"></i>
                  References ({qa.references.length})
                </span>
                <i className={`fa-solid fa-chevron-${isReferencesOpen ? "up" : "down"}`} />
              </div>

              {isReferencesOpen && (
                <div className="accordion-body">
                  <div className="journal-style-refs">
                    {qa.references.map((ref, i) => {
                      const refHref = ref.source_link || ref.pubmed_url || (ref.doi ? `https://doi.org/${ref.doi}` : ref.video_url || "#");
                      const publication = ref.journal || ref.organization || ref.specialty;
                      return (
                        <div key={i} className={`journal-ref-card${ref.is_multimodal ? ' multimodal-ref' : ''}`}>
                          <div className="ref-number">
                            {ref.chunk_type === 'table' ? (
                              <i className="fa-solid fa-table" title="Table" style={{color: '#CB2323', fontSize: '11px'}}></i>
                            ) : ref.chunk_type === 'figure_description' ? (
                              <i className="fa-solid fa-image" title="Figure" style={{color: '#CB2323', fontSize: '11px'}}></i>
                            ) : VIDEO_CHUNK_TYPES.has(ref.chunk_type) ? (
                              <i className="fa-solid fa-video" title="Video" style={{color: '#2583A9', fontSize: '11px'}}></i>
                            ) : (
                              <span className="ref-bullet">→</span>
                            )}
                          </div>
                          <div className="ref-content">
                            {publication && (
                              <div className="ref-row">
                                <span className="ref-label">Publication:</span>
                                <span className="ref-value ref-publication-value">{publication}{ref.year ? ` · ${ref.year}` : ''}</span>
                              </div>
                            )}
                            <div className="ref-row">
                              <span className="ref-label">Title:</span>
                              <a href={refHref} target="_blank" rel="noreferrer" className="ref-title-link">
                                {ref.title || ref.display_text || "Untitled"}
                              </a>
                            </div>
                            {ref.authors && (
                              <div className="ref-row">
                                <span className="ref-label">Authors:</span>
                                <span className="ref-value ref-authors-value">{ref.authors}</span>
                              </div>
                            )}

                            {/* Video timestamp + link */}
                            {VIDEO_CHUNK_TYPES.has(ref.chunk_type) && (
                              <div className="ref-video-meta">
                                {ref.video_title && ref.video_title !== ref.title && (
                                  <div className="ref-row">
                                    <span className="ref-label">Video:</span>
                                    <span className="ref-value">{ref.video_title}</span>
                                  </div>
                                )}
                                <div className="ref-video-actions">
                                  {(ref.start_time_formatted || ref.end_time_formatted) && (
                                    <span className="ref-video-timestamp">
                                      <i className="fa-solid fa-clock" style={{marginRight: '4px', fontSize: '11px'}}></i>
                                      {ref.start_time_formatted}{ref.end_time_formatted ? ` – ${ref.end_time_formatted}` : ''}
                                    </span>
                                  )}
                                  {ref.video_url && (
                                    <a href={ref.video_url} target="_blank" rel="noreferrer" className="ref-video-link">
                                      <i className="fa-solid fa-play" style={{marginRight: '4px', fontSize: '11px'}}></i>
                                      Play at timestamp
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Expandable details — only for multimodal refs (tables/figures/images) */}
                            {(ref.is_multimodal || ref.image_blob_url || ref.chunk_type === 'table' || ref.chunk_type === 'figure_description') && (
                              <>
                                <button className="ref-expand-btn" onClick={() => setExpandedRefs(prev => ({ ...prev, [i]: !prev[i] }))}>
                                  {expandedRefs[i] ? 'Hide preview' : (() => {
                                    const contentLabel = ref.chunk_type === 'table' ? 'Table' : ref.chunk_type === 'figure_description' ? 'Figure' : 'Content';
                                    const nameHint = ref.title || ref.display_text || null;
                                    const shortName = nameHint && nameHint.length > 40 ? nameHint.slice(0, 40).trimEnd() + '…' : nameHint;
                                    return shortName ? `Preview ${contentLabel}: ${shortName}` : `Preview ${contentLabel}`;
                                  })()}
                                  <i className={`fa-solid fa-chevron-${expandedRefs[i] ? 'up' : 'down'}`}></i>
                                </button>
                                {expandedRefs[i] && (
                                  <div className="ref-expanded-details">
                                    {ref.image_blob_url && (
                                      <div className="ref-image-row">
                                        <img src={ref.image_blob_url} alt={ref.title || 'Figure'} className="ref-image-preview"
                                          onClick={() => setMultimodalModal({ show: true, citation: ref })} />
                                      </div>
                                    )}
                                    {(ref.enriched_text || ref.snippet) && (
                                      <div className="ref-row">
                                        <span className="ref-label">Excerpt:</span>
                                        <span className="ref-value ref-excerpt-value">{ref.enriched_text || ref.snippet}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* BACKGROUND SOURCES — retrieved but not cited inline.
              Same row layout as References, but visually de-emphasized via the
              `background-refs` class on the wrapper so it doesn't compete with
              the cited bibliography for the reader's attention. */}
          {qa.backgroundReferences?.length > 0 && (
            <div className="accordion-section background-refs">
              <div
                className="accordion-header"
                onClick={() => setIsBackgroundReferencesOpen(!isBackgroundReferencesOpen)}
              >
                <span className="section-title">
                  <i className="fa-solid fa-layer-group"></i>
                  Background sources ({qa.backgroundReferences.length})
                </span>
                <i className={`fa-solid fa-chevron-${isBackgroundReferencesOpen ? "up" : "down"}`} />
              </div>

              {isBackgroundReferencesOpen && (
                <div className="accordion-body">
                  <p className="background-refs-note">
                    Retrieved by the search system but not directly cited in the answer above. Provided as additional context.
                  </p>
                  <div className="journal-style-refs">
                    {qa.backgroundReferences.map((ref, i) => {
                      const refHref = ref.source_link || ref.pubmed_url || (ref.doi ? `https://doi.org/${ref.doi}` : ref.video_url || "#");
                      const publication = ref.journal || ref.organization || ref.specialty;
                      return (
                        <div key={i} className={`journal-ref-card${ref.is_multimodal ? ' multimodal-ref' : ''}`}>
                          <div className="ref-number">
                            {ref.chunk_type === 'table' ? (
                              <i className="fa-solid fa-table" title="Table" style={{color: '#CB2323', fontSize: '11px'}}></i>
                            ) : ref.chunk_type === 'figure_description' ? (
                              <i className="fa-solid fa-image" title="Figure" style={{color: '#CB2323', fontSize: '11px'}}></i>
                            ) : VIDEO_CHUNK_TYPES.has(ref.chunk_type) ? (
                              <i className="fa-solid fa-video" title="Video" style={{color: '#2583A9', fontSize: '11px'}}></i>
                            ) : (
                              <span className="ref-bullet">→</span>
                            )}
                          </div>
                          <div className="ref-content">
                            {publication && (
                              <div className="ref-row">
                                <span className="ref-label">Publication:</span>
                                <span className="ref-value ref-publication-value">{publication}{ref.year ? ` · ${ref.year}` : ''}</span>
                              </div>
                            )}
                            <div className="ref-row">
                              <span className="ref-label">Title:</span>
                              <a href={refHref} target="_blank" rel="noreferrer" className="ref-title-link">
                                {ref.title || ref.display_text || "Untitled"}
                              </a>
                            </div>
                            {ref.authors && (
                              <div className="ref-row">
                                <span className="ref-label">Authors:</span>
                                <span className="ref-value ref-authors-value">{ref.authors}</span>
                              </div>
                            )}

                            {VIDEO_CHUNK_TYPES.has(ref.chunk_type) && (
                              <div className="ref-video-meta">
                                {ref.video_title && ref.video_title !== ref.title && (
                                  <div className="ref-row">
                                    <span className="ref-label">Video:</span>
                                    <span className="ref-value">{ref.video_title}</span>
                                  </div>
                                )}
                                <div className="ref-video-actions">
                                  {(ref.start_time_formatted || ref.end_time_formatted) && (
                                    <span className="ref-video-timestamp">
                                      <i className="fa-solid fa-clock" style={{marginRight: '4px', fontSize: '11px'}}></i>
                                      {ref.start_time_formatted}{ref.end_time_formatted ? ` – ${ref.end_time_formatted}` : ''}
                                    </span>
                                  )}
                                  {ref.video_url && (
                                    <a href={ref.video_url} target="_blank" rel="noreferrer" className="ref-video-link">
                                      <i className="fa-solid fa-play" style={{marginRight: '4px', fontSize: '11px'}}></i>
                                      Play at timestamp
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                            {(ref.is_multimodal || ref.image_blob_url || ref.chunk_type === 'table' || ref.chunk_type === 'figure_description') && (
                              <>
                                <button className="ref-expand-btn" onClick={() => setExpandedBgRefs(prev => ({ ...prev, [i]: !prev[i] }))}>
                                  {expandedBgRefs[i] ? 'Hide preview' : (() => {
                                    const contentLabel = ref.chunk_type === 'table' ? 'Table' : ref.chunk_type === 'figure_description' ? 'Figure' : 'Content';
                                    const nameHint = ref.title || ref.display_text || null;
                                    const shortName = nameHint && nameHint.length > 40 ? nameHint.slice(0, 40).trimEnd() + '…' : nameHint;
                                    return shortName ? `Preview ${contentLabel}: ${shortName}` : `Preview ${contentLabel}`;
                                  })()}
                                  <i className={`fa-solid fa-chevron-${expandedBgRefs[i] ? 'up' : 'down'}`}></i>
                                </button>
                                {expandedBgRefs[i] && (
                                  <div className="ref-expanded-details">
                                    {ref.image_blob_url && (
                                      <div className="ref-image-row">
                                        <img src={ref.image_blob_url} alt={ref.title || 'Figure'} className="ref-image-preview"
                                          onClick={() => setMultimodalModal({ show: true, citation: ref })} />
                                      </div>
                                    )}
                                    {(ref.enriched_text || ref.snippet) && (
                                      <div className="ref-row">
                                        <span className="ref-label">Excerpt:</span>
                                        <span className="ref-value ref-excerpt-value">{ref.enriched_text || ref.snippet}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FOLLOW-UP QUESTIONS — stays in main view */}
{qa.followup_questions?.length > 0 && (
  <div className="accordion-section">
    <div
      className="accordion-header"
      onClick={() => setIsFollowupsOpen(!isFollowupsOpen)}
    >
      <span className="section-title">
        <i className="fa-solid fa-circle-question"></i>
        Explore Further
      </span>
      <i className={`fa-solid fa-chevron-${isFollowupsOpen ? "up" : "down"}`} />
    </div>

    {isFollowupsOpen && (
      <div className="accordion-body">
        <div className="followup-grid">
          {qa.followup_questions.slice(0, showAllFollowups ? 4 : 2).map((fq, i) => {
            const isExpanded = enrichCardIndex === i;
            const entities = threadContext?.clinicalEntities || null;

            const demographics = entities?.demographics || {};
            const missingData = entities
              ? ['age', 'gender'].filter(k => !demographics[k]).map(k => k.charAt(0).toUpperCase() + k.slice(1))
              : [];

            const composeQuery = (factors, context, decisionInputs) => {
              const parts = [];
              if (context.trim()) parts.push(context.trim());
              const selected = Object.entries(factors).filter(([, v]) => v).map(([k]) => k);
              if (selected.length > 0) parts.push(`Relevant factors: ${selected.join(', ')}`);
              const filled = Object.entries(decisionInputs)
                .filter(([, v]) => v.trim()).map(([k, v]) => `${k}: ${v.trim()}`);
              if (filled.length > 0) parts.push(filled.join('. '));
              const prefix = parts.length > 0 ? parts.join('. ') + '. — ' : '';
              return prefix + fq;
            };

            const handleExpandCard = () => {
              if (isExpanded) {
                setEnrichCardIndex(null);
                setEnrichFormOpen(false);
                return;
              }
              setEnrichCardIndex(i);
              setEnrichFormOpen(false);
              setEnrichDecisionInputs({});
            };

            const handleAskWithContext = () => {
              handleSuggestionClick(composeQuery({}, enrichContext, enrichDecisionInputs));
              setEnrichCardIndex(null);
            };

            const hasContext = enrichContext.trim().length > 0;
            const hasDecisionInputs = Object.values(enrichDecisionInputs).some(v => v.trim());
            const hasAnyContext = hasContext || hasDecisionInputs;

            if (isExpanded) {
              return (
                <div key={fq + i} className="followup-suggestion-card enrich-expanded">
                  <div className="enrich-header">
                    <span className="followup-card-number">{String(i + 1).padStart(2, '0')}</span>
                    <button className="enrich-collapse-btn" onClick={handleExpandCard} title="Collapse">
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <p className="enrich-question-full">{fq}</p>

                  {!enrichFormOpen ? (
                    <button
                      className="enrich-context-toggle"
                      onClick={() => setEnrichFormOpen(true)}
                    >
                      <i className="fa-solid fa-user-pen" /> Refine with patient info
                    </button>
                  ) : (
                    <div className="enrich-form">
                      <div className="enrich-section">
                        <div className="enrich-section-label">Patient context (optional)</div>
                        <textarea
                          className="enrich-context-input"
                          value={enrichContext}
                          onChange={(e) => setEnrichContext(e.target.value)}
                          placeholder="e.g. 67-year-old with stage IIIB colon cancer, post-hemicolectomy, no prior chemo"
                          rows={3}
                          autoFocus
                        />
                      </div>

                      <div className="enrich-section">
                        <div className="enrich-section-label">Demographics (optional)</div>
                        <div className="enrich-demographics-row">
                          <div className="enrich-demo-field">
                            <label className="enrich-demo-label">Age</label>
                            <div className="enrich-age-input-wrap">
                              <input
                                type="number"
                                min="0"
                                max="120"
                                className="enrich-decision-input enrich-age-input"
                                placeholder="e.g. 67"
                                value={enrichDecisionInputs['Age'] || ''}
                                onChange={(e) => setEnrichDecisionInputs(prev => ({ ...prev, Age: e.target.value }))}
                              />
                              <span className="enrich-age-unit">yrs</span>
                            </div>
                          </div>
                          <div className="enrich-demo-field">
                            <label className="enrich-demo-label">Gender</label>
                            <select
                              className="enrich-decision-input enrich-gender-select"
                              value={enrichDecisionInputs['Gender'] || ''}
                              onChange={(e) => setEnrichDecisionInputs(prev => ({ ...prev, Gender: e.target.value }))}
                            >
                              <option value="">--</option>
                              <option value="Male">M</option>
                              <option value="Female">F</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="enrich-demo-field enrich-demo-field-grow">
                            <label className="enrich-demo-label">Ethnicity</label>
                            <input
                              type="text"
                              className="enrich-decision-input"
                              placeholder="e.g. Hispanic"
                              value={enrichDecisionInputs['Ethnicity'] || ''}
                              onChange={(e) => setEnrichDecisionInputs(prev => ({ ...prev, Ethnicity: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>

                      {missingData.filter(f => !['Age', 'Gender'].includes(f)).length > 0 && (
                        <div className="enrich-section">
                          <div className="enrich-section-label">Context that may help</div>
                          <div className="enrich-decision-fields">
                            {missingData.filter(f => !['Age', 'Gender'].includes(f)).map((field) => (
                              <div key={field} className="enrich-decision-row">
                                <span className="enrich-decision-label">{field}</span>
                                <input
                                  type="text"
                                  className="enrich-decision-input"
                                  placeholder={`Enter ${field.toLowerCase()}…`}
                                  value={enrichDecisionInputs[field] || ''}
                                  onChange={(e) => setEnrichDecisionInputs(prev => ({ ...prev, [field]: e.target.value }))}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {hasAnyContext && (
                        <div className="enrich-preview">
                          <span className="enrich-preview-label">You're asking:</span>
                          <span className="enrich-preview-text">
                            {enrichContext.trim() ? `${enrichContext.trim()} — ` : ''}{fq}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="enrich-actions">
                    <div
                      className="followup-ask-btn enrich-ask-primary"
                      onClick={handleAskWithContext}
                    >
                      Ask this question <i className="fa-solid fa-arrow-right" />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={fq + i}
                className="followup-suggestion-card"
                role="button"
                tabIndex={0}
                onClick={() => handleSuggestionClick(fq)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSuggestionClick(fq);
                  }
                }}
              >
                <div className="followup-card-top">
                  <span className="followup-card-number">{String(i + 1).padStart(2, '0')}</span>
                  <button
                    className="enrich-customize-btn"
                    onClick={(e) => { e.stopPropagation(); handleExpandCard(); }}
                  >
                    <i className="fa-solid fa-user-pen" /> Add patient context
                  </button>
                </div>
                <p className="followup-question-text">{fq}</p>
                <div className="followup-card-cta">
                  <span className="followup-card-cta-text">Ask this question</span>
                  <i className="fa-solid fa-arrow-right followup-card-arrow" />
                </div>
              </div>
            );
          })}
        </div>
        {qa.followup_questions.length > 2 && (
          <button
            className="followup-show-more-btn"
            onClick={() => setShowAllFollowups(v => !v)}
          >
            {showAllFollowups
              ? 'Show fewer'
              : `Show ${Math.min(qa.followup_questions.length - 2, 2)} more`}
            <i className={`fa-solid fa-chevron-${showAllFollowups ? 'up' : 'down'}`} />
          </button>
        )}
      </div>
    )}
  </div>
)}


        </div>
      )}
    </div>
  );
}, (prev, next) =>
  prev.qa === next.qa &&
  prev.index === next.index &&
  prev.copiedIndex === next.copiedIndex &&
  prev.isStreaming === next.isStreaming &&
  prev.showContextFrame === next.showContextFrame &&
  prev.hasContextFrame === next.hasContextFrame &&
  prev.threadContext === next.threadContext &&
  prev.qa?.decisionTreeFigures === next.qa?.decisionTreeFigures
);

  return (
    <div className={`mainWrapper ${isSidebarOpen ? "sidebar-open" : ""} ${firstQuerySubmitted ? "firstQuery" : ""} ${!searchBarForceShow ? "searchbar-hidden-mode" : ""} ${firstQuerySubmitted && !isSidebarOpen ? "has-mini-sidebar" : ""}`}>
    {showSearchGate && <SearchGateModal onDismiss={() => setShowSearchGate(false)} />}





    {/* 👉 Sidebar */}
    <Sidebar
  qaHistory={qaHistory}
  searchHistory={searchHistory}
  clinicalFrame={threadContext?.clinicalFrame || null}
  threadContext={threadContext}
  useGemini={useGemini}
  onUseGeminiChange={setUseGemini}
  onThreadSelect={handleThreadSelect}
  onHistoryItemClick={(query) => handleSearch(query)}
  onNewChatThread={handleNewChatThread}
  onDeepThinkingChange={setIsDeepThinking}
  onSidebarToggle={setIsSidebarOpen}
 />

    {/* Mini Sidebar — icon rail on results page */}
    <MiniSidebar
      visible={(firstQuerySubmitted || (JSON.parse(sessionStorage.getItem("chatThreads")) || []).length > 0) && !isSidebarOpen}
      onOpenSidebar={() => window.openSidebar && window.openSidebar()}
      onNewThread={handleNewChatThread}
      onFocusSearch={() => window.openSidebarSearch && window.openSidebarSearch()}
      onGoHome={() => { sessionStorage.clear(); window.location.replace('/'); }}
      onDiscover={() => navigate('/landing')}
    />

    <div className="chatContainer">

      {/* 🏠 HOME PAGE CENTER UI (Before first search) */}
      {!firstQuerySubmitted && (
  <>
    <div className="landing-hero">
      <h2 className="landing-quote">
        Guideline-Powered Clinical Intelligence
      </h2>
      <p className="landing-subtitle">Deep AI reasoning, grounded in peer-reviewed guidelines.</p>
    </div>
  </>
)}


      {/* 🧠 Context Frame — hidden by default, toggled via button in answer area */}
      {threadContext && threadContext.clinicalFrame && firstQuerySubmitted && showContextFrame && (
        <div className="context-frame-header">
          <div className="context-frame-inner">
            <div className="context-frame-label">
              <i className="fa-solid fa-brain"></i>
              Context Frame
            </div>
            <div className="context-frame-content">{threadContext.clinicalFrame}</div>
            <button className="context-frame-close" onClick={() => setShowContextFrame(false)} title="Hide context frame">
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          {/* Excluded options */}
          {threadContext.excludedOptions?.length > 0 && (
            <div className="context-frame-excluded">
              {threadContext.excludedOptions.map((ex, i) => (
                <span key={`excl-${i}`} className="entity-tag entity-excluded" title="Excluded">
                  <i className="fa-solid fa-ban"></i> {ex}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 📝 Show chat history once search happens */}
      {qaHistory.length > 0 && (
        <div className="qa-history" ref={qaHistoryRef}>
          <div className="history-container" ref={qaContentRef}>
            {qaHistory.map((qa, index) => (
  <QAItem
    key={`${qa.time}-${index}`}
    qa={qa}
    index={index}
    formatAnswer={formatAnswer}
    loadingImage={loadingImage}
    dmilogosmall={dmilogosmall}
    handleCopy={handleCopy}
    copiedIndex={copiedIndex}
    handleSuggestionClick={handleSuggestionClick}
    setShowFeedbackPopup={setShowFeedbackPopup}
    setCitationPopup={setCitationPopup}
    setMultimodalModal={setMultimodalModal}
    isStreaming={loading && index === qaHistory.length - 1}
    showContextFrame={showContextFrame}
    onToggleContextFrame={() => setShowContextFrame(prev => !prev)}
    hasContextFrame={!!(threadContext && threadContext.clinicalFrame)}
    threadContext={threadContext}
    onAnswerNow={handleAnswerNow}
    showAnswerNow={isDeepThinking && loading && index === qaHistory.length - 1}
  />
))}

          </div>
        </div>
      )}

      {/* Search Bar area — wraps the bar + toggle button together */}
      {firstQuerySubmitted ? (
        <div className={`searchBar-bottom-row ${isSidebarOpen ? "sidebar-open" : ""}`}>
          <button
            className={`deep-thinking-toggle ${isDeepThinking ? 'active' : ''}`}
            onClick={() => setIsDeepThinking(prev => !prev)}
            title={isDeepThinking ? "Deep Thinking enabled — full reasoning" : "Deep Thinking disabled — quick answer"}
          >
            <i className="fa-solid fa-brain"></i>
            <span className="deep-thinking-label">Deep Thinking</span>
          </button>
          <div className={`searchBar fixed ${!searchBarForceShow ? "searchBar-hidden" : ""}`}>
            <div className="searchInput-wrapper">
              <textarea
                id="searchInput"
                placeholder="Search guidelines for another question..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedObject(null);
                  setRandomAnswer(null);
                }}
                onPaste={() => {}}
                onKeyDown={handleKeyPress}
                disabled={loading}
                rows={3}
              />
            </div>
            <div className="searchBar-toolbar">
              <button className="searchBar-submit" onClick={handleButtonClick} disabled={loading || !query.trim()} title="Ask DeepMedIQ">
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>
          <button
            className="searchBar-reveal-btn"
            onClick={() => setSearchBarForceShow(prev => !prev)}
            title={searchBarForceShow ? "Hide search bar" : "Show search bar"}
          >
            <img src={dmilogosmall} alt="Toggle search" className="reveal-btn-logo" />
          </button>
        </div>
      ) : (
        <div className="searchBar-landing-row">
          <button
            className={`deep-thinking-toggle ${isDeepThinking ? 'active' : ''}`}
            onClick={() => setIsDeepThinking(prev => !prev)}
            title={isDeepThinking ? "Deep Thinking enabled — full reasoning" : "Deep Thinking disabled — quick answer"}
          >
            <i className="fa-solid fa-brain"></i>
            <span className="deep-thinking-label">Deep Thinking</span>
          </button>
          <div className="searchBar">
            <div className="searchInput-wrapper">
              <textarea
                id="searchInput"
                placeholder={window.innerWidth <= 768 ? "Search clinical guidelines..." : "Search guidelines, evidence, and clinical recommendations..."}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedObject(null);
                  setRandomAnswer(null);
                }}
                onPaste={() => {}}
                onKeyDown={handleKeyPress}
                disabled={loading}
                rows={3}
              />
            </div>
            <div className="searchBar-toolbar">
              <button className="searchBar-submit" onClick={handleButtonClick} disabled={loading || !query.trim()} title="Ask DeepMedIQ">
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Queries below search bar on landing */}
      {!firstQuerySubmitted && (
        <div className="landing-discovery">
          <Suggestions onSuggestionClick={handleSuggestionClick} />
        </div>
      )}

      {/* 🔁 Related Questions After Answer */}
      {expandedAnswers && selectedObject?.related_questions && (
        <div className="suggestions-related">
          <h3>You may also like to Search</h3>
          {selectedObject.related_questions.map((rq, index) => (
  <div key={rq.input || index}>
    <button onClick={() => handleRelatedQuestion(rq)}>
      <p className="related-question-item">{rq.input}</p>
    </button>
  </div>
))}

        </div>
      )}

      {/* ❌ No answer found message */}
      {selectedObject && !randomAnswer && (
        <div className="no-answer-found">
          <h2>No answer found for "{displayedQuery}"</h2>
        </div>
      )}

      {/* Ask Now popup — appears on text selection in Q&A area */}
      <AskNowPopup
        containerRef={qaContentRef}
        onAskNow={(selectedText) => handleSearch(selectedText)}
      />

      {/* Footer + Feedback Handling */}
      <Footer />
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        onSubmitFeedback={handleFeedbackSubmit}
      />

      {/* Multimodal Modal (lightbox for tables, figures, decision trees) */}
      <MultimodalModal
        show={multimodalModal.show}
        onClose={() => setMultimodalModal({ show: false, citation: null })}
        citation={multimodalModal.citation}
      />

      {/* Citation Popup */}
      {citationPopup.show && (
        <div 
          className="citation-popup-overlay"
          onClick={() => setCitationPopup({ show: false, citations: [], position: { x: 0, y: 0 } })}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
        >
          <div 
            className="citation-popup"
            style={{
              position: 'absolute',
              top: `${citationPopup.position.y + 10}px`,
              left: `${citationPopup.position.x}px`,
              maxWidth: '500px',
              maxHeight: '60vh',
              overflowY: 'auto',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '16px',
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                <i className="fa-solid fa-book-medical" style={{ marginRight: '8px' }}></i>
                Citation Details
              </h4>
              <button 
                onClick={() => setCitationPopup({ show: false, citations: [], position: { x: 0, y: 0 } })}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                }}
              >
                ×
              </button>
            </div>
            {citationPopup.citations.map((citation, idx) => (
              <div key={idx} style={{ marginBottom: idx < citationPopup.citations.length - 1 ? '16px' : '0', paddingBottom: idx < citationPopup.citations.length - 1 ? '16px' : '0', borderBottom: idx < citationPopup.citations.length - 1 ? '1px solid #eee' : 'none' }}>
                {/* Chunk type badge */}
                {citation.chunk_type && citation.chunk_type !== 'text' && (
                  <div style={{ marginBottom: '8px' }}>
                    <span className={`chunk-type-badge chunk-type-${citation.chunk_type}`}>
                      {citation.chunk_type === 'table' ? '📊 Table'
                        : VIDEO_CHUNK_TYPES.has(citation.chunk_type)
                          ? (citation.chunk_type === 'video_transcript' ? '🎬 Transcript'
                            : citation.chunk_type === 'video_keyframe' ? '🖼️ Keyframe'
                            : '📊 Slide')
                        : '🖼️ Figure'}
                      {citation.visual_type ? ` — ${citation.visual_type}` : ''}
                    </span>
                  </div>
                )}
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  {citation._citationNum && <span style={{ color: '#0066cc', marginRight: '6px' }}>[{citation._citationNum}]</span>}
                  {citation.title}
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                  {citation.authors} • {citation.journal || citation.specialty} • {citation.year}
                </div>
                {/* Section path breadcrumb */}
                {citation.section_path && (
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
                    <i className="fa-solid fa-sitemap" style={{ marginRight: '4px', fontSize: '10px' }}></i>
                    {citation.section_path}
                  </div>
                )}
                {VIDEO_CHUNK_TYPES.has(citation.chunk_type) ? (
                  <div style={{ marginTop: '6px' }}>
                    {(citation.start_time_formatted || citation.end_time_formatted) && (
                      <div style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                        <i className="fa-solid fa-clock" style={{ marginRight: '4px' }}></i>
                        {citation.start_time_formatted}{citation.end_time_formatted ? ` – ${citation.end_time_formatted}` : ''}
                      </div>
                    )}
                    {citation.video_url && (
                      <a
                        href={citation.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '14px', color: '#2583A9', textDecoration: 'none', fontWeight: '600' }}
                      >
                        <i className="fa-solid fa-play" style={{ marginRight: '4px' }}></i>
                        Play at timestamp
                      </a>
                    )}
                  </div>
                ) : (citation.source_link || citation.doi) ? (
                  <a
                    href={citation.source_link || `https://doi.org/${citation.doi}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '14px', color: '#0066cc', textDecoration: 'none' }}
                  >
                    <i className="fa-solid fa-external-link-alt" style={{ marginRight: '4px' }}></i>
                    {citation.doi ? `DOI: ${citation.doi}` : 'View Source'}
                  </a>
                ) : null}
                {/* Image preview for figures — click opens full modal */}
                {citation.image_blob_url && (
                  <div style={{ marginTop: '8px' }}>
                    <img
                      src={citation.image_blob_url}
                      alt={citation.title}
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', border: '1px solid #e0e0e0', cursor: 'pointer' }}
                      onClick={() => {
                        setCitationPopup({ show: false, citations: [], position: { x: 0, y: 0 } });
                        setMultimodalModal({ show: true, citation });
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

}

export default SearchBar;
